import { COLORS, FONTS } from "../../../../../lib/brand";
import { toIsoOrThrow } from "../../../../../lib/datetime";
import { PANE_THEMES } from "../../../../../components/brand/paneThemes";
import type { Href } from "expo-router";
import { router, useFocusEffect, useLocalSearchParams } from "expo-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as React from "react";
import * as Clipboard from "expo-clipboard";
import AsyncStorage from "@react-native-async-storage/async-storage";
import {
  Platform,
  ActivityIndicator,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  useWindowDimensions,
  View,
  type TextStyle,
  type ViewStyle,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import { FriendlyDateTime } from "../../../../../components/client/FriendlyDateTime";

import {
  deleteCompetition,
  getClientCompetition,
  listTeamsForCompetition,
  listRegisteredStudents,
  type TeamSummaryRow,
  moveCompetitionToJudging,
  publishCompetition,
  updateCompetition,
} from "../../../../../lib/clientCompetitions";
import {
  canClientSubmit,
  formatCents,
  listPricingTiers,
  submitForReview,
} from "../../../../../lib/caseApprovals";
import {
  getPendingPromoCode,
  setPendingPromoCode,
  validatePromoCode,
  type PromoValidation,
} from "../../../../../lib/promos";
import { runCompetitionAutoMerge } from "../../../../../lib/admin";
import { AppHeader } from "../../../../../components/AppHeader";
import { Badge, Button, Card, EmptyState, LiveIndicator, Modal, Pill, Section } from "../../../../../components/ui";
import { deleteDeliverable, listDeliverables, reorderDeliverables, updateDeliverable } from "../../../../../lib/deliverables";
import { useAuth } from "../../../../../lib/authContext";
import { isAdminRole } from "../../../../../lib/auth";
import { ADMIN_EDIT_STATUSES, caseEditableFor, isAdminPostSubmitEdit, isClientEditableStatus } from "../../../../../lib/adminCaseEdit";
import { ProgramTimeline, type TimelineDeliverable } from "../../../../../components/client/ProgramTimeline";
import { buildInviteUrl } from "../../../../../lib/invites";
import { inviteJudge, listJudges, removeJudge, updateJudgePermissions } from "../../../../../lib/judges";
import { getCompetitionLeaderboard } from "../../../../../lib/leaderboard";
import {
  competitionHasSubmittedScores,
  getCompetitionRubricWeights,
  setCompetitionRubricWeights,
  type SetRubricWeightsInput,
} from "../../../../../lib/scoring";
import { logAdminAction } from "../../../../../lib/auditLog";
import {
  competitionHasApprovedSelection,
  getCompetitionWinnerConfig,
  saveCompetitionWinnerConfig,
  type PrizeStructureRow,
  type TeamSplitPolicy,
} from "../../../../../lib/winners";
import type { RubricWeightRow } from "../../../../../types/database";
import { listMyShortlist } from "../../../../../lib/shortlist";
import { writeActiveCaseId } from "../../../../../lib/useActiveCase";
import { listAllowedDomains } from "../../../../../lib/emailWhitelist";
import { gradYearOptions, majorsForSchools } from "../../../../../lib/majors";
import { MultiSelectField, SelectField } from "../../../../../components/client/EligibilityPickers";
import type {
  ClientCompetitionListRow,
  CompetitionTier,
  CompetitionType,
  Deliverable,
  EngagementModel,
  Judge,
  LeaderboardRow,
  PricingTier,
  RegisteredStudentListItem,
} from "../../../../../types/database";

const ENGAGEMENT_EDIT: EngagementModel[] = ["licensing", "rm", "hybrid"];

type CompForm = {
  name: string;
  brief: string;
  deliverable_description: string;
  engagement_model: EngagementModel;
  // Client-selectable pricing tier. May be null (not yet picked). Admin
  // can override during approval; we just pre-fill the Quote Builder
  // with whatever the client chose.
  tier: CompetitionTier | null;
  opens_at: string;
  closes_at: string;
  team_size_min: string;
  max_team_size: string;
  competition_type: CompetitionType;
  max_enrollment: string;
  eligibility_grad_year_min: string;
  eligibility_grad_year_max: string;
  eligibility_schools: string[];
  eligibility_majors: string[];
};

function rowToForm(c: ClientCompetitionListRow): CompForm {
  return {
    name: c.name,
    brief: c.brief ?? "",
    deliverable_description: c.deliverable_description ?? "",
    engagement_model: c.engagement_model,
    tier: c.tier ?? null,
    opens_at: c.opens_at ? String(c.opens_at).slice(0, 16) : "",
    closes_at: c.closes_at ? String(c.closes_at).slice(0, 16) : "",
    team_size_min: String(c.team_size_min),
    max_team_size: String(c.max_team_size),
    // Registration model is no longer user-facing. Existing rows keep
    // whatever value they had; brand-new drafts default to 'uncapped'
    // so the submit flow can succeed without exposing the field in UI.
    competition_type: c.competition_type || "uncapped",
    max_enrollment: c.max_enrollment != null ? String(c.max_enrollment) : "",
    eligibility_grad_year_min:
      c.eligibility_grad_year_min != null ? String(c.eligibility_grad_year_min) : "",
    eligibility_grad_year_max:
      c.eligibility_grad_year_max != null ? String(c.eligibility_grad_year_max) : "",
    eligibility_schools: [...(c.eligibility_schools ?? [])],
    eligibility_majors: [...(c.eligibility_majors ?? [])],
  };
}

function RequiredMarker() {
  return <Text className="text-red-600"> *</Text>;
}

function OptionalFieldHint() {
  return (
    <Text className="mt-0.5 text-xs text-primary/50">Optional — leave blank to allow all</Text>
  );
}

function judgeStatusClass(status: Judge["status"]): string {
  switch (status) {
    case "active":
      return "success";
    case "invited":
      return "info";
    case "removed":
    default:
      return "grey";
  }
}

// Section anchor keys. Wired into SectionCard via the `anchor` prop and
// referenced by publish-blockers so clicking a blocker scrolls to its
// owning section.
type SectionAnchor =
  | "basics"
  | "pricing-tier"
  | "promo-code"
  | "eligibility"
  | "schedule-teams"
  | "deliverables"
  | "rubric-weights"
  | "winners-prizes"
  | "judges";

export type PublishBlocker = { message: string; anchor: SectionAnchor };

function computePublishBlockers(
  form: CompForm,
  deliverables: Deliverable[],
  rowStatus: string
): PublishBlocker[] {
  if (rowStatus !== "draft") return [];
  const missing: PublishBlocker[] = [];

  // Basics
  if (!form.name.trim()) missing.push({ message: "Give the case a name.", anchor: "basics" });
  if (!form.brief.trim()) missing.push({ message: "Write the brief — one paragraph describing what students are working on.", anchor: "basics" });
  if (!ENGAGEMENT_EDIT.includes(form.engagement_model)) {
    missing.push({ message: "Pick an engagement model.", anchor: "basics" });
  }

  // Eligibility
  if (form.eligibility_schools.length === 0) {
    missing.push({ message: "Pick at least one eligible school.", anchor: "eligibility" });
  }
  if (form.eligibility_majors.length === 0) {
    missing.push({ message: "Pick at least one eligible major.", anchor: "eligibility" });
  }

  // Schedule
  if (!form.opens_at.trim()) missing.push({ message: "Set when the case opens for registration.", anchor: "schedule-teams" });
  if (!form.closes_at.trim()) missing.push({ message: "Set when the case closes.", anchor: "schedule-teams" });
  if (form.opens_at.trim() && form.closes_at.trim()) {
    const opensTs = new Date(form.opens_at).getTime();
    const closesTs = new Date(form.closes_at).getTime();
    if (Number.isFinite(opensTs) && Number.isFinite(closesTs) && closesTs <= opensTs) {
      missing.push({ message: "Close date must be after open date.", anchor: "schedule-teams" });
    }
  }
  if (form.opens_at.trim()) {
    const opensTs = new Date(form.opens_at).getTime();
    const todayMidnight = new Date();
    todayMidnight.setHours(0, 0, 0, 0);
    if (Number.isFinite(opensTs) && opensTs < todayMidnight.getTime()) {
      missing.push({ message: "Open date must be today or later — pick a future date.", anchor: "schedule-teams" });
    }
  }

  // Team size
  const min = parseInt(form.team_size_min, 10);
  const max = parseInt(form.max_team_size, 10);
  const minOk = Number.isFinite(min) && min >= 1;
  const maxOk = Number.isFinite(max) && max >= 1;
  if (!minOk) missing.push({ message: "Set the minimum team size.", anchor: "schedule-teams" });
  if (!maxOk) missing.push({ message: "Set the maximum team size.", anchor: "schedule-teams" });
  if (minOk && maxOk && min > max) missing.push({ message: "Team size minimum can’t be larger than the maximum.", anchor: "schedule-teams" });

  // Registration style — Open vs Invite only. No further validation
  // needed; pricing tier handles capacity. Legacy "capped" rows from
  // before the simplification still work in the DB but aren't user-
  // selectable anymore.

  // Deliverables
  if (deliverables.length === 0) {
    missing.push({ message: "Add at least one deliverable.", anchor: "deliverables" });
  } else if (!deliverables.some((d) => d.is_scored)) {
    missing.push({ message: "Mark at least one deliverable as scored — judges need something to score.", anchor: "deliverables" });
  }

  // Pricing tier (required for LIC + RM; hidden + skipped for hybrid since
  // hybrid engagement isn't supported in the Quote Builder anyway)
  if (form.engagement_model === "licensing" || form.engagement_model === "rm") {
    if (!form.tier || form.tier.trim() === "") {
      missing.push({ message: "Pick a pricing tier.", anchor: "pricing-tier" });
    }
  }

  return missing;
}

function hasAutoMergeChanges(result: {
  saved_teams: number;
  dissolved_teams: number;
  disqualified_students: number;
  members_moved: number;
}): boolean {
  return (
    result.saved_teams > 0 ||
    result.dissolved_teams > 0 ||
    result.disqualified_students > 0 ||
    result.members_moved > 0
  );
}

function formToPatch(form: CompForm) {
  const min = parseInt(form.team_size_min, 10);
  const max = parseInt(form.max_team_size, 10);
  return {
    name: form.name.trim(),
    brief: form.brief.trim() || null,
    deliverable_description: form.deliverable_description.trim() || null,
    engagement_model: form.engagement_model,
    tier: form.tier,
    opens_at: toIsoOrThrow(form.opens_at, "Opens at"),
    closes_at: toIsoOrThrow(form.closes_at, "Closes at"),
    team_size_min: Number.isFinite(min) && min > 0 ? min : 1,
    max_team_size: Number.isFinite(max) && max > 0 ? max : 1,
    competition_type: form.competition_type,
    max_enrollment:
      form.competition_type === "capped" || form.competition_type === "invite_only"
        ? (() => {
            const n = parseInt(form.max_enrollment, 10);
            return Number.isFinite(n) && n > 0 ? n : null;
          })()
        : null,
    eligibility_schools: form.eligibility_schools.length ? form.eligibility_schools : null,
    eligibility_majors: form.eligibility_majors.length ? form.eligibility_majors : null,
    eligibility_grad_year_min: form.eligibility_grad_year_min.trim()
      ? parseInt(form.eligibility_grad_year_min, 10)
      : null,
    eligibility_grad_year_max: form.eligibility_grad_year_max.trim()
      ? parseInt(form.eligibility_grad_year_max, 10)
      : null,
  };
}

function ChipInput({
  label,
  values,
  onChange,
  hint,
}: {
  label: string;
  values: string[];
  onChange: (v: string[]) => void;
  hint?: string;
}) {
  const [draft, setDraft] = useState("");
  return (
    <View className="mt-3">
      <Text className="text-sm font-medium text-primary">{label}</Text>
      {hint ? <Text className="mt-0.5 text-xs text-primary/50">{hint}</Text> : null}
      <View className="mt-1 flex-row flex-wrap gap-1">
        {values.map((v) => (
          <Pressable
            key={v}
            className="rounded-full bg-primary/10 px-2 py-1"
            onPress={() => onChange(values.filter((x) => x !== v))}
          >
            <Text className="text-xs text-primary">
              {v} <Text className="font-bold">×</Text>
            </Text>
          </Pressable>
        ))}
      </View>
      <View className="mt-2 flex-row gap-2">
        <TextInput
          className="flex-1 rounded-md border border-border bg-white px-2 py-2 text-primary"
          value={draft}
          onChangeText={setDraft}
          placeholder="Add…"
          onSubmitEditing={() => {
            const t = draft.trim();
            if (t && !values.includes(t)) onChange([...values, t]);
            setDraft("");
          }}
        />
        <Pressable
          className="justify-center rounded-md bg-primary px-3"
          onPress={() => {
            const t = draft.trim();
            if (t && !values.includes(t)) onChange([...values, t]);
            setDraft("");
          }}
        >
          <Text className="text-sm font-semibold text-white">Add</Text>
        </Pressable>
      </View>
    </View>
  );
}

export default function CompetitionEditorScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  // Admins reuse this client editor to fix submitted/under_review/approved
  // cases that the client is locked out of (Fix 15). `adminMode` unlocks those
  // statuses; admin RLS authorizes the writes and edits are audited.
  const { role } = useAuth();
  const adminMode = isAdminRole(role);
  const [row, setRow] = useState<ClientCompetitionListRow | null>(null);
  const [deliverables, setDeliverables] = useState<Deliverable[]>([]);
  const [judges, setJudges] = useState<Judge[]>([]);
  const [registeredStudents, setRegisteredStudents] = useState<RegisteredStudentListItem[]>([]);
  const [teams, setTeams] = useState<TeamSummaryRow[]>([]);
  const [shortlistCount, setShortlistCount] = useState(0);
  const [leaderboard, setLeaderboard] = useState<LeaderboardRow[]>([]);
  const [form, setForm] = useState<CompForm | null>(null);
  const [showJudgeForm, setShowJudgeForm] = useState(false);
  const [editingJudgeId, setEditingJudgeId] = useState<string | null>(null);
  const [judgeEmail, setJudgeEmail] = useState("");
  const [judgeCanInvite, setJudgeCanInvite] = useState(false);
  const [judgeCanDeadlines, setJudgeCanDeadlines] = useState(false);
  const [judgeScopeMajors, setJudgeScopeMajors] = useState<string[]>([]);
  const [judgeInviteModalOpen, setJudgeInviteModalOpen] = useState(false);
  const [judgeInviteUrl, setJudgeInviteUrl] = useState("");
  const [judgeInviteExpires, setJudgeInviteExpires] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveHint, setSaveHint] = useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const [schoolOptions, setSchoolOptions] = useState<string[]>([]);
  // Pricing rows for the tier picker. Loaded once on mount — tiny table
  // (8 rows: 4 LIC + 3 RM today) so it's cheap and harmless to refetch.
  const [pricing, setPricing] = useState<PricingTier[]>([]);
  const lastSavedJson = useRef<string>("");
  const formRef = useRef<CompForm | null>(null);
  const hasLoadedRef = useRef(false);
  const lastLoadIdRef = useRef<string | undefined>(undefined);

  const load = useCallback(async () => {
    if (typeof id === "string" && lastLoadIdRef.current !== id) {
      hasLoadedRef.current = false;
      lastLoadIdRef.current = id;
    }
    if (!hasLoadedRef.current) setLoading(true);
    setErr(null);
    try {
      if (typeof id !== "string") return;
      const c = await getClientCompetition(id);
      setRow(c);
      if (c) {
        const f = rowToForm(c);
        setForm(f);
        lastSavedJson.current = JSON.stringify(f);
      } else {
        setForm(null);
      }
      const d = await listDeliverables(id);
      setDeliverables(d);
      const j = await listJudges(id);
      setJudges(j);
      const regs = await listRegisteredStudents(id);
      setRegisteredStudents(regs);
      const teamRows = await listTeamsForCompetition(id);
      setTeams(teamRows);
      const shortlistRows = await listMyShortlist(id);
      setShortlistCount(shortlistRows.length);
      if (c?.status === "judging" || c?.status === "closed") {
        const board = await getCompetitionLeaderboard(id);
        setLeaderboard(board);
      } else {
        setLeaderboard([]);
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load");
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
      setHydrated(true);
    }
  }, [id]);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load])
  );

  // RM programs have no editor — Coravu owns the case design. Belt and
  // braces: the OverviewShell program-nav already hides "Edit case" for
  // RM, but if someone deep-links here we bounce them to /dashboard.
  //
  // Exception: admin-created RM cases (created_by_admin != null) render
  // a read-only view here so the client can see their case at a glance
  // with the "Coravu is managing this case for you" banner. The redirect
  // is preserved for legacy / non-admin-created RM rows.
  useEffect(() => {
    if (
      row &&
      row.engagement_model === "rm" &&
      typeof id === "string" &&
      !row.created_by_admin
    ) {
      router.replace(`/client/competition/${id}/dashboard` as Href);
    }
  }, [row, id]);

  formRef.current = form;

  async function persistSilent(latest: CompForm) {
    // Allow editing in both `draft` and `denied` — denied means the client
    // can revise + resubmit, so autosave needs to keep working. Admins may
    // also edit submitted/under_review/approved cases (the client can't).
    if (typeof id !== "string" || !row) return;
    if (!caseEditableFor(row.status, role)) return;
    setSaving(true);
    setSaveError(null);
    let patch: ReturnType<typeof formToPatch>;
    try {
      patch = formToPatch(latest);
    } catch (e) {
      // A typo in opens_at / closes_at would otherwise throw RangeError and
      // abort the autosave with no signal. Surface it on the save indicator.
      setSaving(false);
      setSaveError(e instanceof Error ? e.message : "A date field isn't valid.");
      return;
    }
    const { error } = await updateCompetition(id, patch);
    setSaving(false);
    if (error) {
      // Distinguish persistent save errors (banner-worthy) from transient
      // ones: surface as the small top-right save indicator instead of
      // an alarming page banner.
      setSaveError(error.message);
      return;
    }
    lastSavedJson.current = JSON.stringify(latest);
    setLastSavedAt(Date.now());
    setSaveHint("Saved");
    setTimeout(() => setSaveHint(null), 1200);
    // Audit admin edits to a submitted/under_review/approved case — the client
    // can't reach this path, so any save here is Coravu fixing the case.
    if (isAdminPostSubmitEdit(row.status, role)) {
      void logAdminAction({
        action: "competition.admin_review_edit",
        entityType: "competitions",
        entityId: id,
        summary: `Edited case "${latest.name || row.name}" while it was ${row.status}`,
      });
    }
    const next = await getClientCompetition(id);
    setRow(next);
  }

  // Manual retry from the "Couldn't save" indicator.
  async function retrySave() {
    const latest = formRef.current;
    if (!latest) return;
    await persistSilent(latest);
  }

  async function onManualSave() {
    if (!form) return;
    await persistSilent(form);
  }

  // Tier picker fires an immediate save (don't wait for the 2.2s autosave
  // debounce) — it's a single-click decision the client may make right
  // before leaving the page. Build the next form synchronously so the
  // persist sees the new value without waiting for setForm to flush.
  async function onTierChange(nextTier: CompetitionTier) {
    if (!form) return;
    if (row && !caseEditableFor(row.status, role)) return;
    const next = { ...form, tier: nextTier };
    setForm(next);
    await persistSilent(next);
  }

  useEffect(() => {
    if (!hydrated || !row) return;
    if (row.status !== "draft" && row.status !== "denied") return;
    const f = formRef.current;
    if (!f) return;
    const serialized = JSON.stringify(f);
    if (serialized === lastSavedJson.current) return;
    const t = setTimeout(() => {
      const latest = formRef.current;
      if (!latest || typeof id !== "string") return;
      if (JSON.stringify(latest) === lastSavedJson.current) return;
      void persistSilent(latest);
    }, 2200);
    return () => clearTimeout(t);
  }, [form, hydrated, id, row]);

  // Load the partner-schools list once, so the eligibility picker matches
  // what students actually have in their profile (sourced from the same
  // allowed_email_domains table they signed up against).
  useEffect(() => {
    let alive = true;
    void (async () => {
      const rows = await listAllowedDomains();
      if (!alive) return;
      const names = Array.from(
        new Set(
          rows
            .map((r) => r.university_name?.trim())
            .filter((n): n is string => !!n && !/test domain/i.test(n))
        )
      ).sort((a, b) => a.localeCompare(b));
      setSchoolOptions(names);
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Pricing tiers for the in-editor TierPicker. Fetched in parallel with
  // the editor itself so the picker has data by the time it renders.
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const rows = await listPricingTiers();
        if (alive) setPricing(rows);
      } catch {
        // Picker will render a "pricing not loaded" hint; the submit-for-
        // review flow doesn't require tier, so this isn't blocking.
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const publishBlockers = useMemo<PublishBlocker[]>(() => {
    if (!row || !form) return [];
    return computePublishBlockers(form, deliverables, row.status);
  }, [row, form, deliverables]);

  const canPublish = row?.status === "draft" && publishBlockers.length === 0;

  async function onPublish() {
    if (typeof id !== "string" || !canPublish) return;
    const { error } = await publishCompetition(id);
    if (error) {
      setErr(error.message);
      return;
    }
    await load();
  }

  // Submit-for-review flow. The same publishBlockers list gates whether the
  // submit button is enabled — clients need to fill out the same required
  // fields whether they're going the legacy direct-publish path or the new
  // approval flow.
  const canSubmit =
    !!row && canClientSubmit(row.status) && publishBlockers.length === 0;
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);
  async function onSubmitForReview() {
    if (typeof id !== "string" || !canSubmit) return;
    try {
      await submitForReview(id);
      setErr(null);
      setSubmitConfirmOpen(true);
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to submit for review");
    }
  }

  // Pure action — confirmation is handled by the surrounding two-click
  // wrapper button (MoveToJudgingButton). On second click we run auto-merge
  // then flip the status to 'judging'.
  async function onMoveToJudging() {
    if (typeof id !== "string" || row?.status !== "open") return;
    let mergeResult;
    try {
      mergeResult = await runCompetitionAutoMerge(id);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to run auto-merge");
      return;
    }
    if (hasAutoMergeChanges(mergeResult)) {
      setSaveHint(
        `${mergeResult.saved_teams} teams saved · ${mergeResult.dissolved_teams} teams dissolved · ${mergeResult.disqualified_students} students disqualified · ${mergeResult.members_moved} members reassigned`
      );
      setTimeout(() => setSaveHint(null), 3200);
    }
    const { error } = await moveCompetitionToJudging(id);
    if (error) {
      setErr(error.message);
      return;
    }
    await load();
  }

  // Pure action — confirmation is handled by the surrounding two-click
  // wrapper button (CloseCompetitionButton).
  async function onCloseCompetition() {
    if (typeof id !== "string" || row?.status !== "judging") return;
    const { error } = await updateCompetition(id, { status: "closed" });
    if (error) {
      setErr(error.message);
      return;
    }
    await load();
  }

  // Delete is gated by a two-click confirm in the button itself (web-friendly,
  // matches the student sign-out pattern in StudentPortalShell). This handler
  // just performs the delete unconditionally — caller is responsible for the
  // confirm UX.
  async function onDeleteCompetition() {
    if (typeof id !== "string" || !row || row.status !== "draft") return;
    setErr(null);
    const { error } = await deleteCompetition(id);
    if (error) {
      setErr(error.message);
      return;
    }
    // Clear the active-case sessionStorage so the smart redirect doesn't try
    // to bounce back into the deleted competition on next /client load.
    writeActiveCaseId(null);
    router.replace("/client" as Href);
  }

  async function moveDeliverable(index: number, dir: -1 | 1) {
    if (typeof id !== "string") return;
    const j = index + dir;
    if (j < 0 || j >= deliverables.length) return;
    const next = [...deliverables];
    [next[index], next[j]] = [next[j], next[index]];
    const { error } = await reorderDeliverables(
      id,
      next.map((d) => d.id)
    );
    if (error) setErr(error.message);
    else setDeliverables(next);
  }

  async function onDeliverableOpensAtChange(deliverableId: string, nextOpensAt: string) {
    // Optimistic local update so the timeline marker follows the cursor
    // immediately, then persist. The case editor's 2.2s autosave doesn't
    // touch deliverables (those have their own table), so we save here.
    setDeliverables((rows) =>
      rows.map((d) => (d.id === deliverableId ? { ...d, opens_at: nextOpensAt } : d))
    );
    const { error } = await updateDeliverable(deliverableId, { opens_at: nextOpensAt });
    if (error) setErr(error.message);
  }

  // Pure action — confirmation is handled by the per-row
  // DeleteDeliverableButton wrapper, which gives each deliverable its own
  // armed state so confirming one doesn't pre-arm another.
  async function onDeleteDeliverable(del: Deliverable) {
    const { error } = await deleteDeliverable(del.id);
    if (error) setErr(error.message);
    else void load();
  }

  function startCreateJudge() {
    setEditingJudgeId(null);
    setJudgeEmail("");
    setJudgeCanInvite(false);
    setJudgeCanDeadlines(false);
    setJudgeScopeMajors([]);
    setShowJudgeForm(true);
  }

  function startEditJudge(j: Judge) {
    setEditingJudgeId(j.id);
    setJudgeEmail(j.email);
    setJudgeCanInvite(j.can_invite_others);
    setJudgeCanDeadlines(j.can_change_deadlines);
    setJudgeScopeMajors([...(j.scope_majors ?? [])]);
    setShowJudgeForm(true);
  }

  async function onSaveJudge() {
    if (typeof id !== "string") return;
    setErr(null);
    if (!editingJudgeId) {
      if (!judgeEmail.trim()) {
        setErr("Judge email is required.");
        return;
      }
      try {
        const { invite } = await inviteJudge({
          competitionId: id,
          email: judgeEmail,
          can_invite_others: judgeCanInvite,
          can_change_deadlines: judgeCanDeadlines,
          scope_majors: judgeScopeMajors.length ? judgeScopeMajors : null,
        });
        setJudgeInviteUrl(buildInviteUrl(invite.token));
        setJudgeInviteExpires(new Date(invite.expires_at).toLocaleString());
        setJudgeInviteModalOpen(true);
      } catch (e) {
        setErr(e instanceof Error ? e.message : "Failed to invite judge");
        return;
      }
    } else {
      const { error } = await updateJudgePermissions(editingJudgeId, {
        can_invite_others: judgeCanInvite,
        can_change_deadlines: judgeCanDeadlines,
        scope_majors: judgeScopeMajors.length ? judgeScopeMajors : null,
      });
      if (error) {
        setErr(error.message);
        return;
      }
    }
    setShowJudgeForm(false);
    setEditingJudgeId(null);
    const j = await listJudges(id);
    setJudges(j);
  }

  async function onRemoveJudge(judgeId: string) {
    const { error } = await removeJudge(judgeId);
    if (error) {
      setErr(error.message);
      return;
    }
    if (typeof id !== "string") return;
    const j = await listJudges(id);
    setJudges(j);
  }

  if (loading && !row) {
    return (
      <SafeAreaView className="flex-1 items-center justify-center bg-black">
        <ActivityIndicator size="large" color={THEME.accent} />
      </SafeAreaView>
    );
  }

  if (!row || !form) {
    return (
      <SafeAreaView className="flex-1 bg-black px-4 pt-4">
        <Text className="text-white">Competition not found.</Text>
        <View className="mt-4 self-start">
          <Button variant="ghost-dark" size="sm" onPress={() => router.replace("/client" as Href)}>Back</Button>
        </View>
      </SafeAreaView>
    );
  }

  const showPublishRequired = row.status === "draft";
  const showLeaderboard = row.status === "judging" || row.status === "closed";
  const leaderboardIsFinal =
    row.status === "closed" || leaderboard.some((r) => r.is_final);

  // Admin-managed RM cases render a read-only view: client can see the
  // shape of their case but can't edit, save, or submit. They're directed
  // to their Coravu account rep to request changes.
  if (row.engagement_model === "rm" && row.created_by_admin) {
    return <AdminManagedRmReadonlyView row={row} />;
  }

  return (
    <CompetitionEditorView
      id={id as string}
      row={row}
      adminMode={adminMode}
      form={form}
      setForm={setForm}
      schoolOptions={schoolOptions}
      pricing={pricing}
      onTierChange={onTierChange}
      saving={saving}
      saveHint={saveHint}
      lastSavedAt={lastSavedAt}
      saveError={saveError}
      onRetrySave={() => void retrySave()}
      err={err}
      deliverables={deliverables}
      registeredStudents={registeredStudents}
      teams={teams}
      shortlistCount={shortlistCount}
      judges={judges}
      leaderboard={leaderboard}
      showLeaderboard={showLeaderboard}
      leaderboardIsFinal={leaderboardIsFinal}
      showPublishRequired={showPublishRequired}
      publishBlockers={publishBlockers}
      canPublish={canPublish}
      canSubmit={canSubmit}
      onSubmitForReview={onSubmitForReview}
      submitConfirmOpen={submitConfirmOpen}
      setSubmitConfirmOpen={setSubmitConfirmOpen}
      onManualSave={onManualSave}
      onPublish={onPublish}
      onMoveToJudging={onMoveToJudging}
      onCloseCompetition={onCloseCompetition}
      onDeleteCompetition={onDeleteCompetition}
      moveDeliverable={moveDeliverable}
      onDeleteDeliverable={onDeleteDeliverable}
      onDeliverableOpensAtChange={onDeliverableOpensAtChange}
      startCreateJudge={startCreateJudge}
      startEditJudge={startEditJudge}
      onSaveJudge={onSaveJudge}
      onRemoveJudge={onRemoveJudge}
      showJudgeForm={showJudgeForm}
      setShowJudgeForm={setShowJudgeForm}
      editingJudgeId={editingJudgeId}
      setEditingJudgeId={setEditingJudgeId}
      judgeEmail={judgeEmail}
      setJudgeEmail={setJudgeEmail}
      judgeCanInvite={judgeCanInvite}
      setJudgeCanInvite={setJudgeCanInvite}
      judgeCanDeadlines={judgeCanDeadlines}
      setJudgeCanDeadlines={setJudgeCanDeadlines}
      judgeScopeMajors={judgeScopeMajors}
      setJudgeScopeMajors={setJudgeScopeMajors}
      judgeInviteModalOpen={judgeInviteModalOpen}
      setJudgeInviteModalOpen={setJudgeInviteModalOpen}
      judgeInviteUrl={judgeInviteUrl}
      judgeInviteExpires={judgeInviteExpires}
    />
  );
}

// ============================================================================
//  CompetitionEditorView
//  Re-skinned dark client-portal version of the editor. All data + handlers
//  are passed in from the parent so the screen state lives in one place.
// ============================================================================

const THEME = PANE_THEMES.Client;
const AMBER = THEME.accent;
const FOREST = THEME.sidebarBg;
const FOREST_MID = COLORS.FOREST_MID.hex;
const OFF_BLACK = THEME.surfaceBg;
const BLACK_SURFACE = "rgba(0,0,0,0.4)";
const LINEN = THEME.textOnSurface;
const W = "rgba(255,255,255,1)";
const WS = "rgba(255,255,255,0.78)";
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const BORDER = "rgba(255,255,255,0.07)";
const SAGE = COLORS.SAGE.hex;
const RED = THEME.destructive;

type CompetitionEditorViewProps = {
  id: string;
  row: ClientCompetitionListRow;
  adminMode: boolean;
  form: CompForm;
  setForm: React.Dispatch<React.SetStateAction<CompForm | null>>;
  schoolOptions: string[];
  pricing: PricingTier[];
  onTierChange: (tier: CompetitionTier) => Promise<void> | void;
  saving: boolean;
  saveHint: string | null;
  lastSavedAt: number | null;
  saveError: string | null;
  onRetrySave: () => void;
  err: string | null;
  deliverables: Deliverable[];
  registeredStudents: RegisteredStudentListItem[];
  teams: TeamSummaryRow[];
  shortlistCount: number;
  judges: Judge[];
  leaderboard: LeaderboardRow[];
  showLeaderboard: boolean;
  leaderboardIsFinal: boolean;
  showPublishRequired: boolean;
  publishBlockers: PublishBlocker[];
  canPublish: boolean;
  canSubmit: boolean;
  onSubmitForReview: () => Promise<void> | void;
  submitConfirmOpen: boolean;
  setSubmitConfirmOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onManualSave: () => Promise<void> | void;
  onPublish: () => Promise<void> | void;
  onMoveToJudging: () => void;
  onCloseCompetition: () => void;
  onDeleteCompetition: () => void;
  moveDeliverable: (i: number, dir: -1 | 1) => Promise<void>;
  onDeleteDeliverable: (d: Deliverable) => void;
  onDeliverableOpensAtChange: (id: string, opensAt: string) => Promise<void>;
  startCreateJudge: () => void;
  startEditJudge: (j: Judge) => void;
  onSaveJudge: () => Promise<void>;
  onRemoveJudge: (id: string) => Promise<void>;
  showJudgeForm: boolean;
  setShowJudgeForm: React.Dispatch<React.SetStateAction<boolean>>;
  editingJudgeId: string | null;
  setEditingJudgeId: React.Dispatch<React.SetStateAction<string | null>>;
  judgeEmail: string;
  setJudgeEmail: React.Dispatch<React.SetStateAction<string>>;
  judgeCanInvite: boolean;
  setJudgeCanInvite: React.Dispatch<React.SetStateAction<boolean>>;
  judgeCanDeadlines: boolean;
  setJudgeCanDeadlines: React.Dispatch<React.SetStateAction<boolean>>;
  judgeScopeMajors: string[];
  setJudgeScopeMajors: React.Dispatch<React.SetStateAction<string[]>>;
  judgeInviteModalOpen: boolean;
  setJudgeInviteModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  judgeInviteUrl: string;
  judgeInviteExpires: string;
};

function CompetitionEditorView(props: CompetitionEditorViewProps) {
  const {
    id, row, adminMode, form, setForm, schoolOptions, pricing, onTierChange, saving, saveHint, lastSavedAt, saveError, onRetrySave, err,
    deliverables, registeredStudents, teams, shortlistCount, judges,
    leaderboard, showLeaderboard, leaderboardIsFinal,
    showPublishRequired, publishBlockers, canPublish,
    canSubmit, onSubmitForReview, submitConfirmOpen, setSubmitConfirmOpen,
    onManualSave, onPublish, onMoveToJudging, onCloseCompetition, onDeleteCompetition,
    moveDeliverable, onDeleteDeliverable, onDeliverableOpensAtChange,
    startCreateJudge, startEditJudge, onSaveJudge, onRemoveJudge,
    showJudgeForm, setShowJudgeForm, editingJudgeId, setEditingJudgeId,
    judgeEmail, setJudgeEmail, judgeCanInvite, setJudgeCanInvite,
    judgeCanDeadlines, setJudgeCanDeadlines, judgeScopeMajors, setJudgeScopeMajors,
    judgeInviteModalOpen, setJudgeInviteModalOpen, judgeInviteUrl, judgeInviteExpires,
  } = props;

  // Editable in draft + denied (the client's window). Admins additionally get
  // submitted/under_review/approved so they can fix a case the client already
  // sent in. `adminEditing` drives the admin chrome + explicit Save below.
  const adminEditing = adminMode && !isClientEditableStatus(row.status) && ADMIN_EDIT_STATUSES.includes(row.status);
  const editable = isClientEditableStatus(row.status) || adminEditing;
  const cursorWeb = Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {};

  // Mobile-aware width gate. The two-column rows (date+time, team-size
  // min/max, grad-year min/max) collapse to single-column below 600px so
  // iPhone-SE-class viewports don't squish them.
  const { width: viewportWidth } = useWindowDimensions();
  const isNarrow = viewportWidth < 600;
  const rowDir: "row" | "column" = isNarrow ? "column" : "row";

  // First-case welcome callout. Dismissed per-user via AsyncStorage so
  // it never reappears once they ask to hide it. AsyncStorage works on
  // web (localStorage shim) and native, so no platform branch needed.
  const [welcomeDismissed, setWelcomeDismissed] = useState<boolean | null>(null);
  useEffect(() => {
    let alive = true;
    void AsyncStorage.getItem("coravu_editor_welcome_dismissed_v1").then((v) => {
      if (!alive) return;
      setWelcomeDismissed(v === "1");
    });
    return () => {
      alive = false;
    };
  }, []);
  function dismissWelcome() {
    void AsyncStorage.setItem("coravu_editor_welcome_dismissed_v1", "1");
    setWelcomeDismissed(true);
  }
  const showWelcome = welcomeDismissed === false && row.status === "draft";

  // Section-anchor system. Each SectionCard registers its layout y-coord
  // here so clicking a publish blocker can scroll the editor to the
  // section that needs the missing field. Also drives a brief highlight
  // pulse via highlightedSection.
  const scrollRef = useRef<ScrollView | null>(null);
  const sectionYRef = useRef<Record<string, number>>({});
  const [highlightedSection, setHighlightedSection] = useState<string | null>(null);
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  function registerSectionY(key: string, y: number) {
    sectionYRef.current[key] = y;
  }
  function scrollToSection(key: string) {
    const y = sectionYRef.current[key];
    if (typeof y !== "number") return;
    scrollRef.current?.scrollTo({ y: Math.max(0, y - 24), animated: true });
    setHighlightedSection(key);
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    highlightTimerRef.current = setTimeout(() => setHighlightedSection(null), 1800);
  }
  useEffect(() => () => {
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
  }, []);

  // "Saved · just now / 2m ago / …" formatter for the top-right save chip.
  const savedAgo = useMemo(() => {
    if (!lastSavedAt) return null;
    const sec = Math.floor((Date.now() - lastSavedAt) / 1000);
    if (sec < 5) return "just now";
    if (sec < 60) return `${sec}s ago`;
    const m = Math.floor(sec / 60);
    if (m < 60) return `${m}m ago`;
    return `${Math.floor(m / 60)}h ago`;
  }, [lastSavedAt, saveHint]);

  const statusTint = (() => {
    switch (row.status) {
      case "open":             return SAGE;
      case "judging":          return AMBER;
      case "closed":           return WM;
      case "submitted":
      case "under_review":     return AMBER;
      case "approved":
      case "contract_pending":
      case "contract_signed":
      case "payment_pending":  return SAGE;
      case "denied":           return AMBER;
      case "denied_terminal":  return RED;
      case "draft":
      default: return WL;
    }
  })();

  // Friendly status label for the pill (raw enum values like "denied_terminal"
  // and "under_review" don't read well in UI).
  const statusLabel = (() => {
    switch (row.status) {
      case "under_review":    return "Under review";
      case "denied_terminal": return "Denied (final)";
      case "contract_pending":return "Contract pending";
      case "contract_signed": return "Contract signed";
      case "payment_pending": return "Payment pending";
      case "final_invoice_pending": return "Final invoice pending";
      case "final_invoice_paid":    return "Final invoice paid";
      default: return row.status;
    }
  })();

  return (
    <>
    <ScrollView ref={scrollRef} style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 80 }} keyboardShouldPersistTaps="handled">
        {/* ADMIN EDIT BAR — only when an admin is editing a case the client
            already submitted. Case fields + eligibility save on this button
            (rubric / winners / deliverables have their own saves). Every save
            is audited and reflects to the client. */}
        {adminEditing ? (
          <View
            style={{
              paddingHorizontal: isNarrow ? 16 : 32,
              paddingVertical: 12,
              flexDirection: isNarrow ? "column" : "row",
              alignItems: isNarrow ? "stretch" : "center",
              gap: 12,
              backgroundColor: "rgba(181,135,58,0.10)",
              borderBottomWidth: 1,
              borderBottomColor: "rgba(181,135,58,0.4)",
            }}
          >
            <View style={{ flex: 1 }}>
              <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 10, letterSpacing: 1.4, textTransform: "uppercase", color: AMBER }}>
                Admin edit · {statusLabel}
              </Text>
              <Text style={{ marginTop: 2, fontFamily: FONTS.body, fontSize: 12, color: WS }}>
                You&apos;re fixing this client&apos;s submitted case. Save case details &amp; eligibility below; rubric, winners &amp; deliverables save in their own sections. Changes are logged and shown to the client.
              </Text>
            </View>
            <PrimaryBtn label={saving ? "Saving…" : "Save changes"} onPress={() => void onManualSave()} disabled={saving} />
          </View>
        ) : null}
        {/* HERO */}
        <View style={{ paddingHorizontal: isNarrow ? 16 : 32, paddingTop: 26, paddingBottom: 18, borderBottomWidth: 1, borderBottomColor: BORDER }}>
          <View style={{ flexDirection: "row", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <View style={{ width: 22, height: 2, backgroundColor: AMBER, opacity: 0.85 }} />
            <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 9.5, letterSpacing: 2, textTransform: "uppercase", color: AMBER }}>
              Case editor
            </Text>
            <View style={{ marginLeft: 10, paddingHorizontal: 9, paddingVertical: 3, borderRadius: 999, borderWidth: 1, borderColor: statusTint }}>
              <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 8.5, letterSpacing: 1.4, textTransform: "uppercase", color: statusTint }}>
                {statusLabel}
              </Text>
            </View>
            <View style={{ flex: 1 }} />
            <SaveIndicator
              saving={saving}
              saveError={saveError}
              savedAgo={savedAgo}
              hasSaved={lastSavedAt != null}
              onRetry={onRetrySave}
            />
          </View>
          <View style={{ flexDirection: isNarrow ? "column" : "row", alignItems: isNarrow ? "flex-start" : "flex-end", gap: isNarrow ? 8 : 16, marginTop: 8 }}>
            <Text style={{ flex: 1, fontFamily: FONTS.display, fontSize: isNarrow ? 26 : 32, lineHeight: isNarrow ? 30 : 36, color: W }} numberOfLines={isNarrow ? 2 : 1}>
              {row.name}
            </Text>
            {/* Shortlist quick-link only makes sense once students have
                actually registered + been considered. Hidden for
                pre-live states (draft / denied / submitted / under_review
                / approved / contract_pending / contract_signed /
                payment_pending) — there's nothing to shortlist yet. */}
            {(["open", "judging", "closed", "final_invoice_pending", "final_invoice_paid"] as const).includes(row.status as never) ? (
              <Pressable
                onPress={() => router.push(`/client/competition/${id}/shortlist` as Href)}
                style={[{ paddingHorizontal: 12, paddingVertical: 7, borderRadius: 3, borderWidth: 1, borderColor: "rgba(181,135,58,0.4)" }, cursorWeb]}
              >
                <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 10, letterSpacing: 1.4, textTransform: "uppercase", color: AMBER }}>
                  Shortlist {shortlistCount}
                </Text>
              </Pressable>
            ) : null}
          </View>
          {row.company_name ? (
            <Text style={{ marginTop: 4, fontFamily: FONTS.bodyLight, fontSize: 12.5, color: WM }}>
              {row.company_name}
            </Text>
          ) : null}
        </View>

        <CaseStatusBanner row={row} />


        {err ? (
          <View style={{ marginHorizontal: isNarrow ? 16 : 32, marginTop: 18, paddingHorizontal: 14, paddingVertical: 12, borderRadius: 3, backgroundColor: "rgba(230,154,138,0.08)", borderWidth: 1, borderColor: "rgba(230,154,138,0.4)" }}>
            <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12.5, color: RED }}>{err}</Text>
          </View>
        ) : null}

        {showWelcome ? (
          <WelcomeCallout isNarrow={!!isNarrow} onDismiss={dismissWelcome} />
        ) : null}

        {/* SECTION: BASICS */}
        <SectionCard
          title="Basics"
          overline="The case"
          description="Tell students what they’ll be working on and what makes it interesting."
          anchor="basics"
          onLayoutY={registerSectionY}
          highlighted={highlightedSection === "basics"}
          isNarrow={isNarrow}
        >
          <FieldLabel
            required={showPublishRequired}
            hint="What students will see as the case title. Be specific — “Reposition the X loyalty program” beats “Marketing case.”"
          >
            Name
          </FieldLabel>
          <DarkInput
            value={form.name}
            onChangeText={(t) => setForm((f) => (f ? { ...f, name: t } : f))}
            editable={editable}
            placeholder="e.g. Q3 Product Strategy Cohort"
            heading
          />
          <View style={{ height: 18 }} />
          <FieldLabel
            required={showPublishRequired}
            hint="One paragraph (3–5 sentences) describing the business problem students will solve. Avoid jargon."
          >
            Brief
          </FieldLabel>
          <DarkInput
            value={form.brief}
            onChangeText={(t) => setForm((f) => (f ? { ...f, brief: t } : f))}
            editable={editable}
            multiline
            minHeight={120}
            placeholder="One paragraph describing the problem you're handing to students."
          />
          <View style={{ height: 18 }} />
          <FieldLabel hint="Optional — paint the picture of the cohort you want to attract.">Target student profile</FieldLabel>
          <DarkInput
            value={form.deliverable_description}
            onChangeText={(t) => setForm((f) => (f ? { ...f, deliverable_description: t } : f))}
            editable={editable}
            multiline
            minHeight={90}
            placeholder="e.g. Junior or senior finance / data majors with prior internship experience."
          />
        </SectionCard>

        {/* SECTION: PRICING TIER */}
        {/* Hidden for hybrid engagement (no pricing rows; Quote Builder also
            refuses hybrid). LIC + RM clients see the picker pre-filled with
            whatever tier is already on the case (admin override or earlier
            client pick), or blank if untouched. */}
        {form.engagement_model === "licensing" || form.engagement_model === "rm" ? (
          <SectionCard
            title="Pricing tier"
            overline="Your pricing plan"
            description="How many students you expect to participate determines your rate. You can change this with Coravu’s approval."
            subtle
            required={showPublishRequired}
            anchor="pricing-tier"
            onLayoutY={registerSectionY}
            highlighted={highlightedSection === "pricing-tier"}
            isNarrow={isNarrow}
          >
            <Text style={{ fontFamily: FONTS.body, fontSize: 12.5, lineHeight: 19, color: WS }}>
              {form.engagement_model === "rm"
                ? "Pick the cohort size you're scoping. Each tier includes a flat RM management fee plus per-student / per-submission rates. Coravu confirms or adjusts during approval."
                : "Pick the cohort size you're scoping. Pricing is per-student plus per-submission. Coravu confirms or adjusts during approval — this is just your starting preference."}
            </Text>
            <View style={{ marginTop: 14 }}>
              <ClientTierPicker
                engagement={form.engagement_model}
                pricing={pricing}
                selected={form.tier}
                disabled={!editable}
                onSelect={(t) => void onTierChange(t)}
              />
            </View>
          </SectionCard>
        ) : null}

        {/* SECTION: PROMO CODE */}
        {form.engagement_model === "licensing" || form.engagement_model === "rm" ? (
          <SectionCard
            title="Promo code"
            overline="Optional discount"
            description="Coravu sometimes shares promo codes for early-bird pricing or partnership programs. Leave blank if you don’t have one."
            subtle
            anchor="promo-code"
            onLayoutY={registerSectionY}
            highlighted={highlightedSection === "promo-code"}
            isNarrow={isNarrow}
          >
            <ClientPromoCodeInput competitionId={id} editable={editable} />
          </SectionCard>
        ) : null}

        {/* SECTION: ELIGIBILITY */}
        <SectionCard
          title="Eligibility"
          overline="Who can register"
          description="Restrict your case to specific schools, majors, or graduation years. Tighter eligibility means a more focused candidate pool."
          subtle
          anchor="eligibility"
          onLayoutY={registerSectionY}
          highlighted={highlightedSection === "eligibility"}
          isNarrow={isNarrow}
        >
          <FieldLabel required={showPublishRequired} hint="Pick at least one partner school whose students should see this case.">Eligible schools</FieldLabel>
          <MultiSelectField
            values={form.eligibility_schools}
            onChange={(v) => setForm((f) => (f ? { ...f, eligibility_schools: v } : f))}
            options={schoolOptions}
            editable={editable}
            placeholder="All partner schools"
            searchPlaceholder="Search schools…"
            modalTitle="Eligible schools"
            emptyHint="No partner schools loaded yet."
          />
          <View style={{ height: 18 }} />
          <FieldLabel
            required={showPublishRequired}
            hint={
              form.eligibility_schools.length > 0
                ? "Pick at least one major. Only majors offered at the selected schools are shown."
                : "Pick at least one eligible major."
            }
          >
            Eligible majors
          </FieldLabel>
          <MultiSelectField
            values={form.eligibility_majors}
            onChange={(v) => setForm((f) => (f ? { ...f, eligibility_majors: v } : f))}
            options={majorsForSchools(form.eligibility_schools)}
            editable={editable}
            placeholder="All majors"
            searchPlaceholder="Search majors…"
            modalTitle="Eligible majors"
          />
          <View style={{ height: 18 }} />
          <View style={{ flexDirection: rowDir, gap: 14 }}>
            <View style={{ flex: 1 }}>
              <FieldLabel hint="Optional. Limit to specific grad years if you’re recruiting a specific cohort (e.g., 2026 for full-time roles, 2027 for internships).">Grad year (min)</FieldLabel>
              <SelectField
                value={form.eligibility_grad_year_min}
                onChange={(v) => setForm((f) => (f ? { ...f, eligibility_grad_year_min: v } : f))}
                options={gradYearOptions()}
                editable={editable}
                placeholder="Any year"
                modalTitle="Earliest grad year"
              />
            </View>
            <View style={{ flex: 1 }}>
              <FieldLabel hint="Optional. Leave open to include later cohorts.">Grad year (max)</FieldLabel>
              <SelectField
                value={form.eligibility_grad_year_max}
                onChange={(v) => setForm((f) => (f ? { ...f, eligibility_grad_year_max: v } : f))}
                options={gradYearOptions()}
                editable={editable}
                placeholder="Any year"
                modalTitle="Latest grad year"
              />
            </View>
          </View>

          {/* Registration style — Open (default) vs Invite-only.
              "First-come, first-served capped" was redundant: the pricing
              tier above already caps registrations, and Open mode is
              naturally first-come-first-served once the tier is reached.
              "Capped" is preserved in the DB enum for any legacy cases. */}
          <View style={{ height: 18 }} />
          <FieldLabel hint="How students sign up. Most cases use Open registration — anyone eligible signs up, up to your pricing tier above.">
            Registration style
          </FieldLabel>
          <View style={{ marginTop: 8, flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
            {([
              ["uncapped", "Open — anyone eligible can register"],
              ["invite_only", "Invite only — specific students"],
            ] as Array<[CompetitionType, string]>).map(([ct, label]) => {
              const selected = form.competition_type === ct;
              return (
                <Pressable
                  key={ct}
                  disabled={!editable}
                  onPress={() => setForm((f) => (f ? { ...f, competition_type: ct } : f))}
                  style={[
                    {
                      paddingHorizontal: 14,
                      paddingVertical: 9,
                      borderRadius: 3,
                      borderWidth: 1,
                      borderColor: selected ? AMBER : BORDER,
                      backgroundColor: selected ? "rgba(181,135,58,0.12)" : BLACK_SURFACE,
                    },
                    editable ? cursorWeb : null,
                    !editable ? { opacity: 0.6 } : null,
                  ]}
                >
                  <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12.5, color: selected ? AMBER : W }}>
                    {label}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        </SectionCard>

        {/* SECTION: SCHEDULE & TEAMS */}
        <SectionCard
          title="Schedule & teams"
          overline="When and how big"
          description="When does registration open and close? Do students work solo or in teams?"
          anchor="schedule-teams"
          onLayoutY={registerSectionY}
          highlighted={highlightedSection === "schedule-teams"}
          isNarrow={isNarrow}
        >
          <ProgramTimeline
            opensAt={form.opens_at}
            closesAt={form.closes_at}
            onOpensAtChange={(v) => setForm((f) => (f ? { ...f, opens_at: v } : f))}
            onClosesAtChange={(v) => setForm((f) => (f ? { ...f, closes_at: v } : f))}
            deliverables={deliverables.map<TimelineDeliverable>((d) => ({
              id: d.id,
              name: d.name,
              sequence: d.sequence,
              opensAt: d.opens_at
                ? String(d.opens_at).slice(0, 16)
                : null,
            }))}
            onDeliverableOpensAtChange={(deliverableId, next) => {
              void onDeliverableOpensAtChange(deliverableId, next);
            }}
            editable={editable}
          />
          <View style={{ height: 18 }} />
          <View style={{ flexDirection: rowDir, gap: 14 }}>
            <View style={{ flex: 1 }}>
              <FieldLabel
                required={showPublishRequired}
                hint="When students can start registering. Pick a date at least one week from today so Coravu can review and the marketing team can promote it."
              >
                Opens at
              </FieldLabel>
              <FriendlyDateTime
                value={form.opens_at}
                onChange={(v) => setForm((f) => (f ? { ...f, opens_at: v } : f))}
                editable={editable}
              />
              <Text style={{ marginTop: 5, fontSize: 10.5, color: WL, fontFamily: FONTS.body }}>
                All times in your local timezone. Students see the same window adjusted to theirs.
              </Text>
            </View>
            <View style={{ flex: 1 }}>
              <FieldLabel
                required={showPublishRequired}
                hint="When registration AND submissions both close. Students do best with 2–4 weeks between open and close — longer cases tend to attract higher-quality submissions."
              >
                Closes at
              </FieldLabel>
              <FriendlyDateTime
                value={form.closes_at}
                onChange={(v) => setForm((f) => (f ? { ...f, closes_at: v } : f))}
                editable={editable}
              />
            </View>
          </View>
          <View style={{ height: 18 }} />
          <View style={{ flexDirection: rowDir, gap: 14 }}>
            <View style={{ flex: 1 }}>
              <FieldLabel
                required={showPublishRequired}
                hint="Solo work = both fields set to 1. Most cases run 2–4 per team."
              >
                Team size (min)
              </FieldLabel>
              <DarkInput
                value={form.team_size_min}
                onChangeText={(t) => setForm((f) => (f ? { ...f, team_size_min: t } : f))}
                editable={editable}
                keyboardType="number-pad"
                placeholder="1"
              />
            </View>
            <View style={{ flex: 1 }}>
              <FieldLabel required={showPublishRequired}>Team size (max)</FieldLabel>
              <DarkInput
                value={form.max_team_size}
                onChangeText={(t) => setForm((f) => (f ? { ...f, max_team_size: t } : f))}
                editable={editable}
                keyboardType="number-pad"
                placeholder="4"
              />
            </View>
          </View>
          {/*
            Registration model + Max enrollment fields removed from the
            UI for MVP simplicity. Defaults are applied silently:
              competition_type = 'uncapped' (anyone eligible can register)
              max_enrollment    = null
            The pricing tier above acts as the implicit capacity guide.
            Power-user features (capped first-come-first-served, invite-only
            cohorts) live behind a future "Advanced settings" panel.
          */}
        </SectionCard>

        {/* SECTION: DELIVERABLES */}
        <SectionCard
          title="Deliverables"
          overline="What students submit"
          description="The work products judges will score. Most cases have 1–3 deliverables (e.g., a slide deck, a financial model, a presentation video)."
          required={showPublishRequired}
          anchor="deliverables"
          onLayoutY={registerSectionY}
          highlighted={highlightedSection === "deliverables"}
          isNarrow={isNarrow}
        >
          {deliverables.length === 0 ? (
            <Text style={{ fontFamily: FONTS.body, fontSize: 13, color: WM }}>
              None yet — add at least one scored deliverable to publish.
            </Text>
          ) : (
            <View style={{ gap: 10 }}>
              {deliverables.map((d, i) => (
                <View key={d.id} style={{ borderWidth: 1, borderColor: BORDER, borderRadius: 4, padding: 14, backgroundColor: BLACK_SURFACE }}>
                  <View style={{ flexDirection: "row", alignItems: "center", gap: 12 }}>
                    <View style={{ width: 30, height: 30, borderRadius: 4, backgroundColor: "rgba(181,135,58,0.10)", borderWidth: 1, borderColor: "rgba(181,135,58,0.32)", alignItems: "center", justifyContent: "center" }}>
                      <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 15, color: AMBER }}>{d.sequence}</Text>
                    </View>
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <View style={{ flexDirection: "row", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                        <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 13.5, color: W }} numberOfLines={1}>{d.name}</Text>
                        {d.is_locked_gate ? <DarkPill label="Gate" tint={AMBER} /> : null}
                        {d.is_scored ? <DarkPill label="Scored" tint={SAGE} /> : null}
                        {!d.allow_revisions ? <DarkPill label="Final only" tint={WM} /> : null}
                      </View>
                      <Text style={{ marginTop: 3, fontFamily: FONTS.body, fontSize: 11.5, color: WM }} numberOfLines={1}>
                        {d.file_type.toUpperCase()}
                        {d.is_scored && d.point_value != null ? ` · ${d.point_value} pts` : ""}
                        {d.due_at ? ` · due ${d.due_at}` : ""}
                      </Text>
                    </View>
                    <View style={{ flexDirection: "row", gap: 4 }}>
                      <SmallIconBtn label="↑" onPress={() => void moveDeliverable(i, -1)} disabled={!editable || i === 0} />
                      <SmallIconBtn label="↓" onPress={() => void moveDeliverable(i, 1)} disabled={!editable || i === deliverables.length - 1} />
                      <SmallTextBtn label="Edit" onPress={() => router.push(`/client/competition/${id}/deliverable/${d.id}` as Href)} tint={AMBER} />
                      <DeleteDeliverableButton del={d} onConfirm={() => void onDeleteDeliverable(d)} disabled={!editable} />
                    </View>
                  </View>
                </View>
              ))}
            </View>
          )}
          <Pressable
            onPress={() => router.push(`/client/competition/${id}/deliverable/new` as Href)}
            style={[{ marginTop: 14, paddingVertical: 12, borderRadius: 3, borderWidth: 1, borderStyle: "dashed", borderColor: "rgba(181,135,58,0.5)", alignItems: "center" }, cursorWeb]}
          >
            <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 11, letterSpacing: 1.4, textTransform: "uppercase", color: AMBER }}>
              + Add deliverable
            </Text>
          </Pressable>
        </SectionCard>

        {/* SECTION: RUBRIC WEIGHTS */}
        <SectionCard
          title="Rubric weights"
          overline="What matters when judges score"
          description="Each criterion is weighted as a percent of the 0–10 Coravu score. Total must equal 100%."
          subtle
          anchor="rubric-weights"
          onLayoutY={registerSectionY}
          highlighted={highlightedSection === "rubric-weights"}
          isNarrow={isNarrow}
        >
          <RubricWeightsEditor competitionId={id} />
        </SectionCard>

        {/* SECTION: WINNERS & PRIZES */}
        <SectionCard
          title="Winners & prizes"
          overline="How many winners and the payout"
          description="Your scholarship pool divides across winning teams. 1st/2nd/3rd is common; some cases include honorable mentions."
          subtle
          anchor="winners-prizes"
          onLayoutY={registerSectionY}
          highlighted={highlightedSection === "winners-prizes"}
          isNarrow={isNarrow}
        >
          <WinnersAndPrizesEditor competitionId={id} adminMode={adminMode} />
        </SectionCard>

        {/* SECTION: JUDGES */}
        <SectionCard
          title="Judges"
          overline="Who scores submissions"
          description="Add at least one judge before the case enters judging. Judges are typically your hiring managers or subject-matter experts. You can add them after Coravu approves the case."
          subtle
          anchor="judges"
          onLayoutY={registerSectionY}
          highlighted={highlightedSection === "judges"}
          isNarrow={isNarrow}
        >
          {judges.length === 0 ? (
            <Text style={{ fontFamily: FONTS.body, fontSize: 13, color: WM }}>
              No judges added yet. You can add judges any time before judging starts.
            </Text>
          ) : (
            <View style={{ gap: 10 }}>
              {judges.map((j) => (
                <View key={j.id} style={{ borderWidth: 1, borderColor: BORDER, borderRadius: 4, padding: 14, backgroundColor: BLACK_SURFACE }}>
                  <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 13, color: W }} numberOfLines={1}>{j.email}</Text>
                      <View style={{ marginTop: 4, flexDirection: "row", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
                        <DarkPill label={j.status} tint={j.status === "active" ? SAGE : j.status === "invited" ? AMBER : WL} />
                        {j.can_invite_others ? <Text style={{ fontFamily: FONTS.body, fontSize: 10.5, color: WM }}>can invite others</Text> : null}
                        {j.can_change_deadlines ? <Text style={{ fontFamily: FONTS.body, fontSize: 10.5, color: WM }}>can change deadlines</Text> : null}
                      </View>
                      {j.scope_majors?.length ? (
                        <Text style={{ marginTop: 3, fontFamily: FONTS.body, fontSize: 10.5, color: WL }}>
                          scoped to: {j.scope_majors.join(", ")}
                        </Text>
                      ) : null}
                    </View>
                    <View style={{ flexDirection: "row", gap: 4 }}>
                      <SmallTextBtn label="Edit" onPress={() => startEditJudge(j)} tint={AMBER} />
                      {j.status !== "removed" ? (
                        <SmallTextBtn label="Remove" onPress={() => void onRemoveJudge(j.id)} tint={RED} />
                      ) : null}
                    </View>
                  </View>
                </View>
              ))}
            </View>
          )}
          {showJudgeForm ? (
            <View style={{ marginTop: 14, padding: 16, backgroundColor: BLACK_SURFACE, borderWidth: 1, borderColor: BORDER, borderRadius: 4 }}>
              <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 18, color: W }}>
                {editingJudgeId ? "Edit judge" : "Invite a judge"}
              </Text>
              {!editingJudgeId ? (
                <>
                  <View style={{ height: 12 }} />
                  <FieldLabel>Email</FieldLabel>
                  <DarkInput value={judgeEmail} onChangeText={setJudgeEmail} editable keyboardType="email-address" autoCapitalize="none" placeholder="judge@example.com" />
                </>
              ) : (
                <Text style={{ marginTop: 8, fontFamily: FONTS.body, fontSize: 12.5, color: WM }}>{judgeEmail}</Text>
              )}
              <View style={{ marginTop: 14, flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
                <Toggle label="Can invite other judges" value={judgeCanInvite} onChange={() => setJudgeCanInvite((v) => !v)} />
                <Toggle label="Can change deadlines" value={judgeCanDeadlines} onChange={() => setJudgeCanDeadlines((v) => !v)} />
              </View>
              <View style={{ marginTop: 14 }}>
                <FieldLabel hint="Optional — leave blank to allow scoring across all majors">Scope by majors</FieldLabel>
                <ChipBox values={judgeScopeMajors} onChange={setJudgeScopeMajors} editable placeholder="Add a major..." />
              </View>
              <View style={{ marginTop: 16, flexDirection: "row", justifyContent: "flex-end", gap: 14 }}>
                <Pressable onPress={() => { setShowJudgeForm(false); setEditingJudgeId(null); }} style={[cursorWeb, { paddingVertical: 8 }]}>
                  <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12, color: WM }}>Cancel</Text>
                </Pressable>
                <Pressable onPress={() => void onSaveJudge()} style={[cursorWeb, { paddingHorizontal: 18, paddingVertical: 10, backgroundColor: AMBER, borderRadius: 3 }]}>
                  <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 11, letterSpacing: 1.6, textTransform: "uppercase", color: FOREST }}>
                    {editingJudgeId ? "Save" : "Send invite"}
                  </Text>
                </Pressable>
              </View>
            </View>
          ) : (
            <Pressable
              onPress={startCreateJudge}
              style={[{ marginTop: 14, paddingVertical: 12, borderRadius: 3, borderWidth: 1, borderStyle: "dashed", borderColor: "rgba(181,135,58,0.5)", alignItems: "center" }, cursorWeb]}
            >
              <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 11, letterSpacing: 1.4, textTransform: "uppercase", color: AMBER }}>
                + Add judge
              </Text>
            </Pressable>
          )}
        </SectionCard>

        {/* SECTION: PEOPLE */}
        {(registeredStudents.length > 0 || teams.length > 0) ? (
          <SectionCard title="Registered people" overline="Live counts" subtle>
            <View style={{ flexDirection: "row", gap: 14, flexWrap: "wrap" }}>
              <StatTile label="Students" value={registeredStudents.length} />
              <StatTile label="Teams" value={teams.length} />
            </View>
            <View style={{ marginTop: 14, flexDirection: "row", gap: 8, flexWrap: "wrap" }}>
              <Pressable
                onPress={() => router.push(`/client/competition/${id}/candidates` as Href)}
                style={[{ paddingHorizontal: 14, paddingVertical: 8, borderRadius: 3, borderWidth: 1, borderColor: BORDER }, cursorWeb]}
              >
                <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11.5, color: W }}>View all candidates →</Text>
              </Pressable>
            </View>
          </SectionCard>
        ) : null}

        {/* SECTION: LEADERBOARD */}
        {showLeaderboard ? (
          <SectionCard title={`Leaderboard ${leaderboardIsFinal ? "(final)" : "(live)"}`} overline="Standings" subtle>
            {leaderboard.length === 0 ? (
              <Text style={{ fontFamily: FONTS.body, fontSize: 13, color: WM }}>No leaderboard rows yet.</Text>
            ) : (
              <View style={{ gap: 8 }}>
                {leaderboard.map((lb) => {
                  const ranked = lb.placement != null;
                  const eliminated = !ranked && lb.eliminated_at_stage != null;
                  return (
                    <Pressable
                      key={lb.team_id}
                      onPress={() => router.push(`/client/team/${lb.team_id}?competitionId=${encodeURIComponent(id)}` as Href)}
                      style={[{ paddingHorizontal: 14, paddingVertical: 12, borderRadius: 3, borderWidth: 1, borderColor: BORDER, backgroundColor: BLACK_SURFACE }, cursorWeb]}
                    >
                      <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
                        <Text style={{ width: 36, fontFamily: FONTS.displayMedium, fontSize: 18, color: ranked ? AMBER : WL }}>
                          {ranked ? `#${lb.placement}` : "—"}
                        </Text>
                        <View style={{ flex: 1, minWidth: 0 }}>
                          <View style={{ flexDirection: "row", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                            <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 13, color: ranked ? W : WM }} numberOfLines={1}>{lb.team_name}</Text>
                            {ranked && lb.final_score != null ? (
                              <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 17, color: AMBER }}>
                                {lb.final_score.toFixed(2)}
                              </Text>
                            ) : null}
                          </View>
                          <Text style={{ marginTop: 2, fontFamily: FONTS.body, fontSize: 11, color: WM }} numberOfLines={1}>
                            {lb.member_count} members
                            {ranked && lb.final_score != null ? " · / 10" : eliminated ? ` · eliminated at stage ${lb.eliminated_at_stage}` : " · pending"}
                          </Text>
                          {lb.category_breakdown && Object.keys(lb.category_breakdown).length > 0 ? (
                            <Text style={{ marginTop: 4, fontFamily: FONTS.body, fontSize: 10.5, color: WL }} numberOfLines={1}>
                              {formatCategoryBreakdown(lb.category_breakdown)}
                            </Text>
                          ) : null}
                        </View>
                      </View>
                    </Pressable>
                  );
                })}
              </View>
            )}
          </SectionCard>
        ) : null}

        {/* WHAT HAPPENS NEXT — only shown pre-active so it's helpful, not noise. */}
        {(row.status === "draft" || row.status === "denied") ? (
          <WhatHappensNext isNarrow={!!isNarrow} />
        ) : null}

        {/* ACTIONS FOOTER */}
        <View style={{ paddingHorizontal: isNarrow ? 16 : 32, paddingTop: 22, marginTop: 6, borderTopWidth: 1, borderTopColor: BORDER }}>
          {(row.status === "draft" || row.status === "denied") &&
          publishBlockers.length > 0 ? (
            <View style={{ marginBottom: 14, paddingHorizontal: 16, paddingVertical: 14, borderRadius: 3, backgroundColor: "rgba(181,135,58,0.06)", borderWidth: 1, borderColor: "rgba(181,135,58,0.28)" }}>
              <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 9.5, letterSpacing: 2, textTransform: "uppercase", color: AMBER }}>
                {publishBlockers.length === 1
                  ? "1 thing left before you can submit"
                  : `${publishBlockers.length} things left before you can submit`}
              </Text>
              <View style={{ marginTop: 8, gap: 4 }}>
                {publishBlockers.map((blocker, i) => (
                  <Pressable
                    key={i}
                    onPress={() => scrollToSection(blocker.anchor)}
                    accessibilityRole="button"
                    accessibilityLabel={`Jump to: ${blocker.message}`}
                    style={[
                      { flexDirection: "row", gap: 8, paddingVertical: 4, borderRadius: 2 } as ViewStyle,
                      Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                    ]}
                  >
                    <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 12, color: AMBER, marginTop: 1 }}>{"·"}</Text>
                    <Text style={{ flex: 1, fontFamily: FONTS.body, fontSize: 12.5, lineHeight: 18, color: WS }}>
                      {blocker.message}
                    </Text>
                    <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 10.5, color: AMBER, alignSelf: "center" }}>→ Jump</Text>
                  </Pressable>
                ))}
              </View>
            </View>
          ) : null}

          <View style={{ flexDirection: isNarrow ? "column" : "row", flexWrap: "wrap", gap: 10, alignItems: isNarrow ? "stretch" : "center" }}>
            {row.status === "draft" || row.status === "denied" ? (
              <>
                <PrimaryBtn
                  label={
                    canSubmit
                      ? row.status === "denied"
                        ? "Resubmit for review"
                        : "Submit for review"
                      : "Submit for review (locked)"
                  }
                  onPress={() => void onSubmitForReview()}
                  disabled={!canSubmit}
                  fullWidth={isNarrow}
                />
                <SecondaryBtn label={saving ? "Saving…" : "Save draft"} onPress={() => void onManualSave()} disabled={saving} fullWidth={isNarrow} />
              </>
            ) : null}
            {row.status === "open" ? (
              <MoveToJudgingButton onConfirm={() => void onMoveToJudging()} fullWidth={isNarrow} />
            ) : null}
            {row.status === "judging" ? (
              <CloseCompetitionButton onConfirm={() => void onCloseCompetition()} fullWidth={isNarrow} />
            ) : null}
            <View style={{ flex: 1 }} />
            {row.status === "draft" || row.status === "denied" ? (
              <DeleteConfirmButton onConfirm={() => void onDeleteCompetition()} />
            ) : null}
          </View>
        </View>
      </ScrollView>

      <Modal
        visible={submitConfirmOpen}
        onClose={() => setSubmitConfirmOpen(false)}
        title="Submitted for review"
        theme="dark"
      >
        <Text style={{ fontFamily: FONTS.body, fontSize: 12.5, color: WS, lineHeight: 19 }}>
          Coravu will review your case within 1–2 business days. We'll email
          you when there's a decision. Until then this case is locked — you'll
          be able to revise it again if it comes back with feedback.
        </Text>
        <View style={{ marginTop: 16 }}>
          <PrimaryBtn label="Got it" onPress={() => setSubmitConfirmOpen(false)} />
        </View>
      </Modal>


      <Modal visible={judgeInviteModalOpen} onClose={() => setJudgeInviteModalOpen(false)} title="Judge invite link" theme="dark">
        <Text style={{ fontFamily: FONTS.body, fontSize: 12.5, color: WS, lineHeight: 19 }}>
          Share this URL with the judge. Email sending is not configured yet — copy and send it yourself.
        </Text>
        <Text style={{ marginTop: 8, fontFamily: FONTS.body, fontSize: 11, color: WL }}>Expires {judgeInviteExpires}</Text>
        <Text selectable style={{ marginTop: 12, padding: 10, borderRadius: 3, borderWidth: 1, borderColor: BORDER, backgroundColor: "#000", color: W, fontFamily: FONTS.body, fontSize: 11 }}>
          {judgeInviteUrl}
        </Text>
        <View style={{ marginTop: 16, gap: 8 }}>
          <PrimaryBtn label="Copy link" onPress={() => void Clipboard.setStringAsync(judgeInviteUrl)} />
          <SecondaryBtn label="Close" onPress={() => setJudgeInviteModalOpen(false)} />
        </View>
      </Modal>
    </>
  );
}

// --- subcomponents -----------------------------------------------------------

/**
 * Banner rendered just below the hero. Shows per-status guidance to the
 * client: pre-active stages explain the queue, denied states surface the
 * Coravu denial_reason, and live/post-live states briefly note that
 * editing is locked.
 *
 * Returns null for `draft` (no banner — the editor is the natural state)
 * and for stages we haven't designed UI for yet.
 */
function CaseStatusBanner({ row }: { row: ClientCompetitionListRow }) {
  const s = row.status;
  if (s === "draft") return null;

  // Denial states — surface the reason verbatim, distinguish revisable
  // from terminal.
  if (s === "denied" || s === "denied_terminal") {
    const terminal = s === "denied_terminal";
    const tone = terminal ? RED : AMBER;
    return (
      <BannerShell tone={tone}>
        <BannerTitle tone={tone}>
          {terminal ? "Case permanently denied" : "Coravu denied this case"}
        </BannerTitle>
        {row.denial_reason ? (
          <Text style={{ marginTop: 6, fontFamily: FONTS.body, fontSize: 12.5, lineHeight: 18, color: LINEN }}>
            <Text style={{ color: WM }}>Reason: </Text>
            {row.denial_reason}
          </Text>
        ) : null}
        <Text style={{ marginTop: 8, fontFamily: FONTS.body, fontSize: 11.5, color: WS }}>
          {terminal
            ? "This decision is final. If you have questions, contact support@coravu.com."
            : "Revise the fields below and resubmit when you're ready."}
        </Text>
      </BannerShell>
    );
  }

  // Pre-active queue states — the editor is locked while Coravu reviews
  // and the contract / payment flow runs.
  const queueCopy: Record<string, { title: string; body: string }> = {
    submitted: {
      title: "Submitted for review",
      body: "Coravu will review your case within 1–2 business days. You'll get an email when there's a decision.",
    },
    under_review: {
      title: "Coravu is reviewing your case",
      body: "A Coravu admin is actively looking at this case. You'll hear back shortly.",
    },
    approved: {
      title: "Approved — next: sign the contract",
      body: "Coravu approved your case and sent over a quote. The contract + initial payment unlock the program to go live.",
    },
    contract_pending: {
      title: "Contract pending signature",
      body: "Check your email for the DocuSign envelope. Once everyone signs, you'll be prompted to make the initial payment.",
    },
    contract_signed: {
      title: "Contract signed — next: initial payment",
      body: "Pay the initial amount on file to open the case for student registration.",
    },
    payment_pending: {
      title: "Waiting for initial payment",
      body: "Once payment clears, your case opens for student registration automatically.",
    },
  };
  const cm = queueCopy[s];
  if (cm) {
    return (
      <BannerShell tone={AMBER}>
        <BannerTitle tone={AMBER}>{cm.title}</BannerTitle>
        <Text style={{ marginTop: 6, fontFamily: FONTS.body, fontSize: 12, lineHeight: 18, color: WS }}>
          {cm.body}
        </Text>
        <Text style={{ marginTop: 6, fontFamily: FONTS.body, fontSize: 11, color: WL }}>
          Editing is locked while this case is in queue.
        </Text>
      </BannerShell>
    );
  }

  // Live + post-live states — same locked-fields hint the editor used to
  // show inline. Subtle (no amber rail) since this isn't a "you need to
  // act" state.
  return (
    <BannerShell tone="rgba(255,255,255,0.16)">
      <Text style={{ fontFamily: FONTS.body, fontSize: 12, color: WM }}>
        This case is {s.replace(/_/g, " ")}. Most fields are locked — you can still manage judges and view submissions.
      </Text>
    </BannerShell>
  );
}

function BannerShell({ tone, children }: { tone: string; children: React.ReactNode }) {
  // Tone color is used as a left-edge rail (4px) + a 30%-opacity border.
  // The fill is a desaturated tint of the same color so banners feel like
  // amber/red/grey "channels" rather than overpowering hero panels.
  const fill =
    tone === RED
      ? "rgba(230,154,138,0.06)"
      : tone === AMBER
        ? "rgba(181,135,58,0.06)"
        : "rgba(255,255,255,0.02)";
  return (
    <View
      style={{
        marginHorizontal: 32,
        marginTop: 18,
        paddingHorizontal: 14,
        paddingVertical: 12,
        borderRadius: 3,
        borderWidth: 1,
        borderColor: tone === RED || tone === AMBER ? tone : BORDER,
        borderLeftWidth: 3,
        borderLeftColor: tone,
        backgroundColor: fill,
      }}
    >
      {children}
    </View>
  );
}

function BannerTitle({ tone, children }: { tone: string; children: React.ReactNode }) {
  return (
    <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 10, letterSpacing: 1.4, textTransform: "uppercase", color: tone }}>
      {children}
    </Text>
  );
}

function SectionCard({
  title, overline, description, subtle, required, anchor, onLayoutY, highlighted, isNarrow, children,
}: {
  title: string;
  overline: string;
  /** Plain-English "why this section matters" line under the title. */
  description?: string;
  subtle?: boolean;
  required?: boolean;
  /** Key used by publish-blockers to scroll to this section + briefly tint it. */
  anchor?: string;
  /** Registers the section's y-coordinate with the parent so scrolling
   *  to a blocker can find it. */
  onLayoutY?: (key: string, y: number) => void;
  /** True for ~1.8s after a blocker click — pulses the border. */
  highlighted?: boolean;
  isNarrow?: boolean;
  children: React.ReactNode;
}) {
  const paddingH = isNarrow ? 16 : 32;
  return (
    <View
      style={{ paddingHorizontal: paddingH, paddingTop: 26 }}
      onLayout={(e) => {
        if (anchor && onLayoutY) onLayoutY(anchor, e.nativeEvent.layout.y);
      }}
    >
      <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
        <View style={{ width: 18, height: 2, backgroundColor: AMBER, opacity: 0.7 }} />
        <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 9, letterSpacing: 1.8, textTransform: "uppercase", color: AMBER }}>
          {overline}
        </Text>
        {required ? <Text style={{ color: RED, fontFamily: FONTS.bodyBold, fontSize: 9 }}>*</Text> : null}
      </View>
      <Text style={{ marginTop: 4, fontFamily: FONTS.display, fontSize: 22, lineHeight: 26, color: W }}>
        {title}
      </Text>
      {description ? (
        <Text style={{ marginTop: 4, fontFamily: FONTS.body, fontSize: 13, lineHeight: 19, color: WS, maxWidth: 640 }}>
          {description}
        </Text>
      ) : null}
      <View
        style={{
          marginTop: 14,
          padding: isNarrow ? 16 : 22,
          borderRadius: 4,
          backgroundColor: subtle ? "rgba(255,255,255,0.015)" : OFF_BLACK,
          borderWidth: 1,
          borderColor: highlighted ? AMBER : BORDER,
          // boxShadow on web for the highlight pulse — falls through on native.
          ...(highlighted && Platform.OS === "web"
            ? ({ boxShadow: "0 0 0 3px rgba(181,135,58,0.18)" } as unknown as object)
            : {}),
        }}
      >
        {children}
      </View>
    </View>
  );
}

// Radio-style tier picker for the client-side editor. Mirrors the admin
// Quote Builder's TierPicker so an admin opening the case post-submission
// sees the same selection the client made. LIC offers 100/250/500/
// unlimited; RM offers 100/250/500 (no unlimited row in pricing_tiers)
// and surfaces the flat RM management fee per option.
function ClientTierPicker({
  engagement,
  pricing,
  selected,
  disabled,
  onSelect,
}: {
  engagement: "licensing" | "rm";
  pricing: PricingTier[];
  selected: CompetitionTier | null;
  disabled?: boolean;
  onSelect: (tier: CompetitionTier) => void;
}) {
  const cursorWeb = Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {};
  const allTiers: CompetitionTier[] =
    engagement === "rm" ? ["100", "250", "500"] : ["100", "250", "500", "unlimited"];

  return (
    <View
      style={{
        borderRadius: 4,
        borderWidth: 1,
        borderColor: BORDER,
        backgroundColor: BLACK_SURFACE,
        overflow: "hidden",
        opacity: disabled ? 0.55 : 1,
      }}
    >
      {allTiers.map((tier, idx) => {
        const row = pricing.find((p) => p.tier === tier && p.engagement_model === engagement);
        const isSelected = selected === tier;
        return (
          <Pressable
            key={tier}
            onPress={() => {
              if (disabled) return;
              onSelect(tier);
            }}
            disabled={disabled}
            style={[
              {
                flexDirection: "row",
                alignItems: "center",
                gap: 12,
                paddingHorizontal: 14,
                paddingVertical: 12,
                borderTopWidth: idx === 0 ? 0 : 1,
                borderTopColor: BORDER,
                backgroundColor: isSelected ? "rgba(181,135,58,0.10)" : "transparent",
              },
              disabled ? null : cursorWeb,
            ]}
          >
            <View
              style={{
                width: 14,
                height: 14,
                borderRadius: 7,
                borderWidth: 1,
                borderColor: isSelected ? AMBER : WL,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {isSelected ? (
                <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: AMBER }} />
              ) : null}
            </View>
            <Text
              style={{
                fontFamily: FONTS.bodySemiBold,
                fontSize: 13,
                color: isSelected ? AMBER : LINEN,
                minWidth: 110,
              }}
            >
              {tier === "unlimited" ? "Unlimited" : `${tier} students`}
            </Text>
            {row ? (
              <Text
                style={{
                  flex: 1,
                  fontFamily: FONTS.body,
                  fontSize: 12,
                  color: isSelected ? WS : WM,
                }}
              >
                {clientTierRateSummary(row)}
              </Text>
            ) : (
              <Text style={{ fontFamily: FONTS.body, fontSize: 12, color: WL }}>
                Pricing not loaded
              </Text>
            )}
          </Pressable>
        );
      })}
    </View>
  );
}

// "$5,000 flat + $4.99/student + $1.50/submission + $10/team presentation"
// Same shape as the admin Quote Builder's tierRateSummary helper.
function clientTierRateSummary(row: PricingTier): string {
  const parts: string[] = [];
  if (row.flat_rm_fee_cents != null) parts.push(`${formatCents(row.flat_rm_fee_cents)} flat`);
  parts.push(`${formatCents(row.per_student_rate_cents)}/student`);
  parts.push(`${formatCents(row.per_submission_rate_cents)}/submission`);
  if (row.per_presentation_rate_cents != null) {
    parts.push(`${formatCents(row.per_presentation_rate_cents)}/team presentation`);
  }
  return parts.join(" · ");
}

function FieldLabel({ children, required, hint }: { children: string; required?: boolean; hint?: string }) {
  return (
    <View>
      <View style={{ flexDirection: "row", alignItems: "baseline", gap: 5 }}>
        <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 9, letterSpacing: 1.8, textTransform: "uppercase", color: WS }}>
          {children}
        </Text>
        {required ? <Text style={{ color: RED, fontFamily: FONTS.bodyBold, fontSize: 9 }}>*</Text> : null}
      </View>
      {hint ? <Text style={{ marginTop: 3, fontFamily: FONTS.body, fontSize: 10.5, color: WL }}>{hint}</Text> : null}
    </View>
  );
}

function DarkInput({
  value, onChangeText, editable = true, multiline, minHeight, placeholder, keyboardType, autoCapitalize, heading,
}: {
  value: string;
  onChangeText: (t: string) => void;
  editable?: boolean;
  multiline?: boolean;
  minHeight?: number;
  placeholder?: string;
  keyboardType?: "default" | "number-pad" | "email-address";
  autoCapitalize?: "none" | "sentences" | "words" | "characters";
  heading?: boolean;
}) {
  return (
    <TextInput
      value={value}
      onChangeText={onChangeText}
      editable={editable}
      multiline={multiline}
      keyboardType={keyboardType}
      autoCapitalize={autoCapitalize}
      placeholder={placeholder}
      placeholderTextColor={WL}
      textAlignVertical={multiline ? "top" : "auto"}
      style={{
        marginTop: 8,
        backgroundColor: BLACK_SURFACE,
        borderWidth: 1,
        borderColor: BORDER,
        borderRadius: 3,
        paddingHorizontal: 14,
        paddingVertical: 12,
        minHeight: minHeight ?? undefined,
        color: editable ? W : WM,
        fontFamily: heading ? FONTS.displayMedium : FONTS.body,
        fontSize: heading ? 18 : 13.5,
        lineHeight: heading ? undefined : 20,
        opacity: editable ? 1 : 0.7,
      }}
    />
  );
}

function ChipBox({
  values, onChange, editable, placeholder,
}: {
  values: string[];
  onChange: (v: string[]) => void;
  editable: boolean;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState("");
  const cursorWeb = Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {};
  return (
    <View style={{ marginTop: 8 }}>
      {values.length > 0 ? (
        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
          {values.map((v, i) => (
            <View key={`${v}-${i}`} style={{ flexDirection: "row", alignItems: "center", paddingHorizontal: 10, paddingVertical: 5, borderRadius: 999, borderWidth: 1, borderColor: "rgba(181,135,58,0.4)", backgroundColor: "rgba(181,135,58,0.08)", gap: 7 }}>
              <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11.5, color: AMBER }}>{v}</Text>
              {editable ? (
                <Pressable onPress={() => onChange(values.filter((_, j) => j !== i))} style={cursorWeb}>
                  <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 12, color: AMBER, lineHeight: 12 }}>×</Text>
                </Pressable>
              ) : null}
            </View>
          ))}
        </View>
      ) : null}
      {editable ? (
        <View style={{ flexDirection: "row", gap: 8 }}>
          <TextInput
            value={draft}
            onChangeText={setDraft}
            placeholder={placeholder}
            placeholderTextColor={WL}
            style={{
              flex: 1,
              backgroundColor: BLACK_SURFACE,
              borderWidth: 1,
              borderColor: BORDER,
              borderRadius: 3,
              paddingHorizontal: 12,
              paddingVertical: 10,
              color: W,
              fontFamily: FONTS.body,
              fontSize: 13,
            }}
            onSubmitEditing={() => {
              const t = draft.trim();
              if (t && !values.includes(t)) onChange([...values, t]);
              setDraft("");
            }}
          />
          <Pressable
            onPress={() => {
              const t = draft.trim();
              if (t && !values.includes(t)) onChange([...values, t]);
              setDraft("");
            }}
            style={[{ paddingHorizontal: 16, justifyContent: "center", borderRadius: 3, borderWidth: 1, borderColor: "rgba(181,135,58,0.5)" }, cursorWeb]}
          >
            <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 10, letterSpacing: 1.4, textTransform: "uppercase", color: AMBER }}>Add</Text>
          </Pressable>
        </View>
      ) : null}
    </View>
  );
}

function DarkPill({ label, tint }: { label: string; tint: string }) {
  return (
    <View style={{ paddingHorizontal: 7, paddingVertical: 2.5, borderRadius: 999, borderWidth: 1, borderColor: tint, backgroundColor: "rgba(255,255,255,0.04)" }}>
      <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 8, letterSpacing: 1.4, textTransform: "uppercase", color: tint }}>{label}</Text>
    </View>
  );
}

function SmallIconBtn({ label, onPress, disabled }: { label: string; onPress: () => void; disabled?: boolean }) {
  const cursorWeb = Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {};
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      style={[
        { paddingHorizontal: 8, paddingVertical: 5, borderRadius: 3, borderWidth: 1, borderColor: BORDER, opacity: disabled ? 0.35 : 1 },
        !disabled ? cursorWeb : null,
      ]}
    >
      <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11, color: WM }}>{label}</Text>
    </Pressable>
  );
}

function SmallTextBtn({ label, onPress, tint, disabled }: { label: string; onPress: () => void; tint: string; disabled?: boolean }) {
  const cursorWeb = Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {};
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      style={[
        { paddingHorizontal: 9, paddingVertical: 5, borderRadius: 3, borderWidth: 1, borderColor: tint + "55", opacity: disabled ? 0.35 : 1 },
        !disabled ? cursorWeb : null,
      ]}
    >
      <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 9.5, letterSpacing: 1.2, textTransform: "uppercase", color: tint }}>{label}</Text>
    </Pressable>
  );
}

function Toggle({ label, value, onChange }: { label: string; value: boolean; onChange: () => void }) {
  const cursorWeb = Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {};
  return (
    <Pressable
      onPress={onChange}
      style={[
        {
          paddingHorizontal: 12,
          paddingVertical: 7,
          borderRadius: 3,
          borderWidth: 1,
          borderColor: value ? AMBER : BORDER,
          backgroundColor: value ? "rgba(181,135,58,0.12)" : BLACK_SURFACE,
        },
        cursorWeb,
      ]}
    >
      <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11.5, color: value ? AMBER : W }}>{label}</Text>
    </Pressable>
  );
}

function StatTile({ label, value }: { label: string; value: number }) {
  return (
    <View style={{ paddingHorizontal: 18, paddingVertical: 14, backgroundColor: BLACK_SURFACE, borderRadius: 3, borderWidth: 1, borderColor: BORDER, minWidth: 140 }}>
      <Text style={{ fontFamily: FONTS.display, fontSize: 28, color: W }}>{value}</Text>
      <Text style={{ marginTop: 4, fontFamily: FONTS.bodyBold, fontSize: 9, letterSpacing: 1.6, textTransform: "uppercase", color: WL }}>{label}</Text>
    </View>
  );
}

function PrimaryBtn({ label, onPress, disabled, fullWidth }: { label: string; onPress: () => void; disabled?: boolean; fullWidth?: boolean }) {
  const cursorWeb = Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {};
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      style={[
        {
          paddingHorizontal: 22,
          paddingVertical: 13,
          borderRadius: 3,
          backgroundColor: disabled ? "rgba(181,135,58,0.35)" : AMBER,
          alignItems: fullWidth ? "center" : "flex-start",
          alignSelf: fullWidth ? "stretch" : "auto",
          minHeight: 44, // Apple HIG tap-target floor
        },
        !disabled ? cursorWeb : null,
      ]}
    >
      <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 12, letterSpacing: 1.6, textTransform: "uppercase", color: FOREST }}>{label}</Text>
    </Pressable>
  );
}

function SecondaryBtn({ label, onPress, disabled, fullWidth }: { label: string; onPress: () => void; disabled?: boolean; fullWidth?: boolean }) {
  const cursorWeb = Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {};
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      style={[
        {
          paddingHorizontal: 18,
          paddingVertical: 11,
          borderRadius: 3,
          borderWidth: 1,
          borderColor: BORDER,
          opacity: disabled ? 0.5 : 1,
          alignItems: fullWidth ? "center" : "flex-start",
          alignSelf: fullWidth ? "stretch" : "auto",
          minHeight: 44,
        },
        !disabled ? cursorWeb : null,
      ]}
    >
      <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11.5, letterSpacing: 1, color: W }}>{label}</Text>
    </Pressable>
  );
}

// ─── Save indicator + Welcome + What-happens-next ──────────────────────

function SaveIndicator({
  saving,
  saveError,
  savedAgo,
  hasSaved,
  onRetry,
}: {
  saving: boolean;
  saveError: string | null;
  savedAgo: string | null;
  hasSaved: boolean;
  onRetry: () => void;
}) {
  const cursorWeb = Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {};
  if (saving) {
    return (
      <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
        <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: SAGE, opacity: 0.85 }} />
        <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11, color: SAGE }}>Saving…</Text>
      </View>
    );
  }
  if (saveError) {
    return (
      <Pressable onPress={onRetry} style={[{ flexDirection: "row", alignItems: "center", gap: 6 }, cursorWeb]}>
        <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: RED }} />
        <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11, color: RED }}>Couldn’t save · Retry</Text>
      </Pressable>
    );
  }
  if (hasSaved) {
    return (
      <Text style={{ fontFamily: FONTS.body, fontSize: 11, color: WS }}>
        Saved · {savedAgo ?? "just now"}
      </Text>
    );
  }
  return null;
}

function WelcomeCallout({ isNarrow, onDismiss }: { isNarrow: boolean; onDismiss: () => void }) {
  const cursorWeb = Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {};
  return (
    <View
      style={{
        marginHorizontal: isNarrow ? 16 : 32,
        marginTop: 18,
        padding: 18,
        borderRadius: 4,
        borderWidth: 1,
        borderColor: "rgba(181,135,58,0.36)",
        backgroundColor: "rgba(181,135,58,0.06)",
      }}
    >
      <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 10, letterSpacing: 1.8, textTransform: "uppercase", color: AMBER }}>
        Welcome to your first Coravu case
      </Text>
      <Text style={{ marginTop: 8, fontFamily: FONTS.body, fontSize: 13, lineHeight: 19, color: WS, maxWidth: 640 }}>
        Fill out the sections below. Your work autosaves as you go — no need to manually save.
        When everything is ready, click <Text style={{ color: LINEN, fontFamily: FONTS.bodyMedium }}>Submit for review</Text>{" "}
        and Coravu will review your case and send you a quote within 1–2 business days.
      </Text>
      <Pressable
        onPress={onDismiss}
        accessibilityRole="button"
        accessibilityLabel="Hide this welcome message for future cases"
        style={[{ marginTop: 10, alignSelf: "flex-start", paddingVertical: 4 }, cursorWeb]}
      >
        <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11.5, color: AMBER }}>
          Hide this for future cases →
        </Text>
      </Pressable>
    </View>
  );
}

function WhatHappensNext({ isNarrow }: { isNarrow: boolean }) {
  const steps: string[] = [
    "Coravu reviews your case (1–2 business days)",
    "You receive a quote + initial payment link",
    "You sign a contract via DocuSign",
    "You pay the initial fee via Stripe",
    "Your case goes live and students start registering",
  ];
  return (
    <View style={{ paddingHorizontal: isNarrow ? 16 : 32, marginTop: 28 }}>
      <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
        <View style={{ width: 18, height: 2, backgroundColor: AMBER, opacity: 0.7 }} />
        <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 9, letterSpacing: 1.8, textTransform: "uppercase", color: AMBER }}>
          After you submit
        </Text>
      </View>
      <Text style={{ marginTop: 4, fontFamily: FONTS.display, fontSize: 22, lineHeight: 26, color: W }}>
        What happens next
      </Text>
      <View
        style={{
          marginTop: 14,
          padding: isNarrow ? 16 : 22,
          borderRadius: 4,
          backgroundColor: "rgba(255,255,255,0.015)",
          borderWidth: 1,
          borderColor: BORDER,
        }}
      >
        {steps.map((step, i) => (
          <View
            key={i}
            style={{
              flexDirection: "row",
              gap: 12,
              paddingVertical: 6,
              alignItems: "flex-start",
            }}
          >
            <View
              style={{
                width: 22,
                height: 22,
                borderRadius: 11,
                borderWidth: 1,
                borderColor: AMBER,
                backgroundColor: "rgba(181,135,58,0.08)",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 12, color: AMBER }}>{i + 1}</Text>
            </View>
            <Text style={{ flex: 1, fontFamily: FONTS.body, fontSize: 13, lineHeight: 22, color: WS }}>
              {step}
            </Text>
          </View>
        ))}
        <Text style={{ marginTop: 10, fontFamily: FONTS.body, fontSize: 12, color: WM }}>
          Questions? Email <Text style={{ color: AMBER }}>hello@coravu.com</Text>.
        </Text>
      </View>
    </View>
  );
}

// Two-click confirm button for destructive actions. Same pattern as the
// student-portal sign-out: first click swaps the label and arms a 4-second
// auto-cancel; second click within that window fires the action.
function DeleteConfirmButton({ onConfirm }: { onConfirm: () => void }) {
  const [armed, setArmed] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cursorWeb = Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {};

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <Pressable
      onPress={() => {
        if (armed) {
          if (timerRef.current) clearTimeout(timerRef.current);
          setArmed(false);
          onConfirm();
        } else {
          setArmed(true);
          timerRef.current = setTimeout(() => setArmed(false), 4000);
        }
      }}
      style={[cursorWeb, { paddingVertical: 8, paddingHorizontal: 4 }]}
    >
      <Text
        style={{
          fontFamily: armed ? FONTS.bodyBold : FONTS.bodyMedium,
          fontSize: 11.5,
          letterSpacing: armed ? 1 : 0.5,
          textTransform: armed ? "uppercase" : "none",
          color: RED,
        }}
      >
        {armed ? "Click again to confirm delete" : "Delete competition"}
      </Text>
    </Pressable>
  );
}

// Two-click confirm wrapper around the Move-to-judging primary button.
// Same pattern as DeleteConfirmButton — we keep the legacy Alert.alert flow
// off web (where it silently no-ops) and replace it with an in-page confirm.
function MoveToJudgingButton({ onConfirm, fullWidth }: { onConfirm: () => void; fullWidth?: boolean }) {
  const [armed, setArmed] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);
  return (
    <PrimaryBtn
      label={armed ? "Click again to confirm move to judging" : "Move to judging →"}
      fullWidth={fullWidth}
      onPress={() => {
        if (armed) {
          if (timerRef.current) clearTimeout(timerRef.current);
          setArmed(false);
          onConfirm();
        } else {
          setArmed(true);
          timerRef.current = setTimeout(() => setArmed(false), 4000);
        }
      }}
    />
  );
}

// Two-click confirm wrapper around the Close-competition primary button.
function CloseCompetitionButton({ onConfirm, fullWidth }: { onConfirm: () => void; fullWidth?: boolean }) {
  const [armed, setArmed] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);
  return (
    <PrimaryBtn
      label={armed ? "Click again to confirm close" : "Close competition"}
      fullWidth={fullWidth}
      onPress={() => {
        if (armed) {
          if (timerRef.current) clearTimeout(timerRef.current);
          setArmed(false);
          onConfirm();
        } else {
          setArmed(true);
          timerRef.current = setTimeout(() => setArmed(false), 4000);
        }
      }}
    />
  );
}

// Two-click confirm wrapper around a deliverable's red "Delete" button.
// Each row gets its own armed state so confirming one doesn't pre-arm
// another deliverable's delete.
function DeleteDeliverableButton({
  del,
  onConfirm,
  disabled,
}: {
  del: Deliverable;
  onConfirm: () => void;
  disabled?: boolean;
}) {
  const [armed, setArmed] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);
  // Reset armed state if the deliverable changes underneath (e.g. reorder
  // shuffles which row this component instance is bound to).
  useEffect(() => {
    setArmed(false);
    if (timerRef.current) clearTimeout(timerRef.current);
  }, [del.id]);
  return (
    <SmallTextBtn
      label={armed ? "Click again" : "Delete"}
      tint={RED}
      disabled={disabled}
      onPress={() => {
        if (armed) {
          if (timerRef.current) clearTimeout(timerRef.current);
          setArmed(false);
          onConfirm();
        } else {
          setArmed(true);
          timerRef.current = setTimeout(() => setArmed(false), 4000);
        }
      }}
    />
  );
}

// Friendly short labels for the rubric category slugs in the leaderboard
// breakdown line. Keeps the row compact — full names live in the rubric
// editor.
const CATEGORY_SHORT_LABELS: Record<string, string> = {
  team_efficiency: "Team eff",
  deliverable_quality: "Quality",
  innovation_solution: "Innovation",
  presentation_communication: "Presentation",
};

function formatCategoryBreakdown(breakdown: Record<string, number>): string {
  const order = ["team_efficiency", "deliverable_quality", "innovation_solution", "presentation_communication"];
  const parts: string[] = [];
  for (const slug of order) {
    if (breakdown[slug] != null) {
      const label = CATEGORY_SHORT_LABELS[slug] ?? slug;
      parts.push(`${label} ${breakdown[slug].toFixed(1)}`);
    }
  }
  // Tail any extra slugs we don't know about so future categories don't get
  // silently dropped.
  for (const [slug, val] of Object.entries(breakdown)) {
    if (!order.includes(slug)) parts.push(`${slug} ${val.toFixed(1)}`);
  }
  return parts.join(" · ");
}

// ─── Rubric weights editor ───────────────────────────────────────────────
// Reads the comp's current weights, lets the client edit them with live
// total + presets, and saves via set_competition_rubric_weights RPC. The
// RPC enforces sum=100 (deferred constraint trigger) and refuses edits
// once any submitted scores exist for the competition (which would
// retroactively re-rank teams). UI grays itself out in that case.

const RUBRIC_PRESETS: { label: string; weights: Record<string, number> }[] = [
  {
    label: "Balanced",
    weights: {
      team_efficiency: 25,
      deliverable_quality: 25,
      innovation_solution: 25,
      presentation_communication: 25,
    },
  },
  {
    label: "Technical-heavy",
    weights: {
      team_efficiency: 30,
      deliverable_quality: 50,
      innovation_solution: 15,
      presentation_communication: 5,
    },
  },
  {
    label: "Presentation-heavy",
    weights: {
      team_efficiency: 15,
      deliverable_quality: 15,
      innovation_solution: 20,
      presentation_communication: 50,
    },
  },
  {
    label: "Innovation-heavy",
    weights: {
      team_efficiency: 15,
      deliverable_quality: 15,
      innovation_solution: 55,
      presentation_communication: 15,
    },
  },
];

function RubricWeightsEditor({ competitionId }: { competitionId: string | undefined }) {
  const [weights, setWeights] = useState<RubricWeightRow[]>([]);
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [locked, setLocked] = useState(false);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const cursorWeb = Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {};

  const refresh = useCallback(async () => {
    if (!competitionId) return;
    setLoading(true);
    const [w, hasSubmitted] = await Promise.all([
      getCompetitionRubricWeights(competitionId),
      competitionHasSubmittedScores(competitionId),
    ]);
    setWeights(w);
    const initial: Record<string, string> = {};
    for (const row of w) initial[row.id] = String(row.weight_pct);
    setInputs(initial);
    setLocked(hasSubmitted);
    setLoading(false);
  }, [competitionId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const total = useMemo(
    () =>
      weights.reduce((sum, w) => {
        const n = Number(inputs[w.id] ?? "0");
        return Number.isFinite(n) ? sum + n : sum;
      }, 0),
    [weights, inputs]
  );

  const dirty = useMemo(
    () => weights.some((w) => Number(inputs[w.id] ?? "0") !== w.weight_pct),
    [weights, inputs]
  );

  function setOne(catId: string, raw: string) {
    const cleaned = raw.replace(/[^0-9]/g, "").slice(0, 3);
    setInputs((prev) => ({ ...prev, [catId]: cleaned }));
    setStatusMsg(null);
    setErrMsg(null);
  }

  function applyPreset(preset: { label: string; weights: Record<string, number> }) {
    const next: Record<string, string> = {};
    for (const w of weights) {
      const presetVal = preset.weights[w.slug];
      next[w.id] = String(presetVal ?? w.weight_pct);
    }
    setInputs(next);
    setStatusMsg("Applied " + preset.label + " preset — review and save.");
    setErrMsg(null);
  }

  async function save() {
    if (!competitionId) return;
    if (total !== 100) {
      setErrMsg("Weights must total 100% (currently " + total + "%).");
      return;
    }
    setSaving(true);
    setErrMsg(null);
    setStatusMsg(null);
    const payload: SetRubricWeightsInput = {
      competitionId,
      weights: weights.map((w) => ({
        category_id: w.id,
        weight_pct: Number(inputs[w.id] ?? "0"),
      })),
    };
    const { error } = await setCompetitionRubricWeights(payload);
    setSaving(false);
    if (error) {
      setErrMsg(error.message);
      return;
    }
    // Audit log — admin-only via RLS, so this is a no-op for client users
    // (logAdminAction warns and returns null). The diff captures the
    // pre/post weights for the four canonical categories so an admin can
    // see exactly what changed.
    const beforeBySlug: Record<string, number> = {};
    const afterBySlug: Record<string, number> = {};
    for (const w of weights) {
      beforeBySlug[w.slug] = w.weight_pct;
      afterBySlug[w.slug] = Number(inputs[w.id] ?? "0");
    }
    void logAdminAction({
      action: "rubric.weights_set",
      entityType: "competitions",
      entityId: competitionId,
      summary: "Set rubric weights",
      diff: { before: beforeBySlug, after: afterBySlug },
    });
    setStatusMsg("Saved.");
    setTimeout(() => setStatusMsg(null), 1800);
    await refresh();
  }

  if (loading) {
    return (
      <View style={{ paddingVertical: 8 }}>
        <ActivityIndicator size="small" color={AMBER} />
      </View>
    );
  }

  if (weights.length === 0) {
    return (
      <Text style={{ fontFamily: FONTS.body, fontSize: 13, color: WM }}>
        No rubric categories are configured yet. Reach out to support.
      </Text>
    );
  }

  const totalTint = total === 100 ? SAGE : AMBER;

  return (
    <View>
      <Text style={{ fontFamily: FONTS.body, fontSize: 12.5, lineHeight: 19, color: WS }}>
        Tell us what matters. The weights determine how each judge category contributes to the
        final Coravu score on a 0–10 scale.
      </Text>

      {locked ? (
        <View
          style={{
            marginTop: 12,
            paddingHorizontal: 14,
            paddingVertical: 10,
            borderRadius: 4,
            borderWidth: 1,
            borderColor: "rgba(181,135,58,0.36)",
            backgroundColor: "rgba(181,135,58,0.08)",
          }}
        >
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 9.5,
              letterSpacing: 1.6,
              textTransform: "uppercase",
              color: AMBER,
            }}
          >
            Weights are locked
          </Text>
          <Text
            style={{
              marginTop: 4,
              fontFamily: FONTS.body,
              fontSize: 12,
              color: WS,
              lineHeight: 18,
            }}
          >
            Judging is in progress — changing weights now would retroactively re-rank teams.
            Contact Tyler if this needs to change.
          </Text>
        </View>
      ) : null}

      <View style={{ marginTop: 14, flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
        {RUBRIC_PRESETS.map((p) => (
          <Pressable
            key={p.label}
            onPress={() => applyPreset(p)}
            disabled={locked}
            style={[
              {
                paddingHorizontal: 12,
                paddingVertical: 7,
                borderRadius: 999,
                borderWidth: 1,
                borderColor: BORDER,
                backgroundColor: "rgba(255,255,255,0.02)",
                opacity: locked ? 0.4 : 1,
              },
              cursorWeb,
            ]}
          >
            <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11, color: WS }}>
              {p.label}
            </Text>
          </Pressable>
        ))}
      </View>

      <View style={{ marginTop: 14, gap: 10 }}>
        {weights.map((w) => (
          <View
            key={w.id}
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 12,
              paddingHorizontal: 14,
              paddingVertical: 12,
              borderWidth: 1,
              borderColor: BORDER,
              borderRadius: 4,
              backgroundColor: BLACK_SURFACE,
            }}
          >
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 17, color: W }}>
                {w.name}
              </Text>
              <Text
                style={{
                  marginTop: 3,
                  fontFamily: FONTS.body,
                  fontSize: 11.5,
                  color: WM,
                  lineHeight: 16,
                }}
              >
                {w.description}
              </Text>
            </View>
            <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
              <TextInput
                value={inputs[w.id] ?? ""}
                onChangeText={(v) => setOne(w.id, v)}
                editable={!locked}
                keyboardType="number-pad"
                style={{
                  width: 64,
                  textAlign: "right",
                  backgroundColor: "rgba(0,0,0,0.4)",
                  borderWidth: 1,
                  borderColor: BORDER,
                  borderRadius: 3,
                  paddingHorizontal: 10,
                  paddingVertical: 8,
                  color: W,
                  fontFamily: FONTS.displayMedium,
                  fontSize: 18,
                }}
              />
              <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 13, color: WM }}>%</Text>
            </View>
          </View>
        ))}
      </View>

      <View
        style={{ marginTop: 14, flexDirection: "row", alignItems: "center", gap: 12, flexWrap: "wrap" }}
      >
        <Text
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: 11,
            letterSpacing: 1.4,
            textTransform: "uppercase",
            color: totalTint,
          }}
        >
          Total: {total}%
        </Text>
        {total !== 100 ? (
          <Text style={{ fontFamily: FONTS.body, fontSize: 11, color: WL }}>
            Adjust the inputs so they sum to 100.
          </Text>
        ) : null}
        <View style={{ flex: 1 }} />
        {statusMsg ? (
          <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11, color: SAGE }}>
            {statusMsg}
          </Text>
        ) : null}
        {errMsg ? (
          <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11, color: RED }}>{errMsg}</Text>
        ) : null}
        <Pressable
          onPress={() => void save()}
          disabled={saving || locked || total !== 100 || !dirty}
          style={[
            {
              paddingHorizontal: 18,
              paddingVertical: 10,
              borderRadius: 3,
              backgroundColor: AMBER,
              opacity: saving || locked || total !== 100 || !dirty ? 0.4 : 1,
            },
            cursorWeb,
          ]}
        >
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 11,
              letterSpacing: 1.6,
              textTransform: "uppercase",
              color: FOREST,
            }}
          >
            {saving ? "Saving…" : "Save weights"}
          </Text>
        </Pressable>
      </View>
    </View>
  );
}

// ─── Winners & prizes editor ─────────────────────────────────────────────
// Edits the comp's winner_count + prize_structure jsonb +
// team_split_policy. Locks the form once an approved winner_selection
// exists for the comp — the prize amounts are part of the audit trail at
// that point and changing them out from under approved disbursements is
// not OK. (Tyler can override via SQL if he absolutely has to.)

function WinnersAndPrizesEditor({ competitionId, adminMode }: { competitionId: string | undefined; adminMode?: boolean }) {
  const [winnerCount, setWinnerCount] = useState<number>(1);
  const [rows, setRows] = useState<{ place: number; label: string; amountUsd: string }[]>([
    { place: 1, label: "1st place", amountUsd: "" },
  ]);
  const [splitPolicy, setSplitPolicy] = useState<TeamSplitPolicy>("equal");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [locked, setLocked] = useState(false);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const cursorWeb = Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {};

  const refresh = useCallback(async () => {
    if (!competitionId) return;
    setLoading(true);
    const [config, hasApproved] = await Promise.all([
      getCompetitionWinnerConfig(competitionId),
      competitionHasApprovedSelection(competitionId),
    ]);
    if (config) {
      setWinnerCount(config.winner_count);
      setSplitPolicy(config.team_split_policy);
      const ps = config.prize_structure ?? [];
      const next: { place: number; label: string; amountUsd: string }[] = [];
      for (let i = 1; i <= config.winner_count; i++) {
        const found = ps.find((r) => r.place === i);
        next.push({
          place: i,
          label: found?.label ?? defaultPlaceLabel(i),
          amountUsd: found?.amount_cents != null ? (found.amount_cents / 100).toFixed(2) : "",
        });
      }
      setRows(next);
    }
    setLocked(hasApproved);
    setLoading(false);
  }, [competitionId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  function setCount(n: number) {
    const clamped = Math.max(1, Math.min(10, n));
    setWinnerCount(clamped);
    setRows((prev) => {
      const next = [...prev];
      while (next.length < clamped) {
        const place = next.length + 1;
        next.push({ place, label: defaultPlaceLabel(place), amountUsd: "" });
      }
      while (next.length > clamped) next.pop();
      return next.map((r, i) => ({ ...r, place: i + 1 }));
    });
  }

  function setRow(idx: number, patch: Partial<{ label: string; amountUsd: string }>) {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }

  const totalCents = useMemo(
    () =>
      rows.reduce((sum, r) => {
        const n = Number(r.amountUsd);
        return Number.isFinite(n) ? sum + Math.round(n * 100) : sum;
      }, 0),
    [rows]
  );

  async function save() {
    if (!competitionId) return;
    setErrMsg(null);
    setStatusMsg(null);

    const structure: PrizeStructureRow[] = [];
    for (const r of rows) {
      const n = Number(r.amountUsd);
      if (!Number.isFinite(n) || n <= 0) {
        setErrMsg("Each place needs a positive dollar amount.");
        return;
      }
      structure.push({
        place: r.place,
        amount_cents: Math.round(n * 100),
        label: r.label.trim() || defaultPlaceLabel(r.place),
      });
    }

    setSaving(true);
    const { error } = await saveCompetitionWinnerConfig(competitionId, {
      winner_count: winnerCount,
      prize_structure: structure,
      // Force equal-split — captain-takes-all is no longer a supported
      // option from the client UI. Even if a legacy row had it, we
      // normalize on save.
      team_split_policy: "equal",
    });
    setSaving(false);
    if (error) {
      setErrMsg(error.message);
      return;
    }
    setStatusMsg("Saved.");
    setTimeout(() => setStatusMsg(null), 1800);
    if (adminMode && competitionId) {
      void logAdminAction({
        action: "competition.admin_review_edit",
        entityType: "competitions",
        entityId: competitionId,
        summary: "Edited scholarship split / winners while reviewing the case",
      });
    }
    await refresh();
  }

  if (loading) {
    return (
      <View style={{ paddingVertical: 8 }}>
        <ActivityIndicator size="small" color={AMBER} />
      </View>
    );
  }

  return (
    <View>
      <Text style={{ fontFamily: FONTS.body, fontSize: 12.5, lineHeight: 19, color: WS }}>
        Winners are auto-determined by the leaderboard once all judges submit. Set the count and
        the prize per place — Coravu approves the auto-pick, then disbursements are created per
        student.
      </Text>

      {locked ? (
        <View
          style={{
            marginTop: 12,
            paddingHorizontal: 14,
            paddingVertical: 10,
            borderRadius: 4,
            borderWidth: 1,
            borderColor: "rgba(181,135,58,0.36)",
            backgroundColor: "rgba(181,135,58,0.08)",
          }}
        >
          <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 9.5, letterSpacing: 1.6, textTransform: "uppercase", color: AMBER }}>
            Locked — winners approved
          </Text>
          <Text style={{ marginTop: 4, fontFamily: FONTS.body, fontSize: 12, color: WS, lineHeight: 18 }}>
            Disbursements have been created for this competition. Contact Tyler if anything here
            needs to change.
          </Text>
        </View>
      ) : null}

      <View style={{ marginTop: 14, flexDirection: "row", alignItems: "center", gap: 12 }}>
        <Text style={{ flex: 1, fontFamily: FONTS.bodyMedium, fontSize: 13, color: W }}>
          Number of winning teams
        </Text>
        <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          <Pressable
            onPress={() => setCount(winnerCount - 1)}
            disabled={locked || winnerCount <= 1}
            style={[
              {
                width: 30,
                height: 30,
                borderRadius: 4,
                borderWidth: 1,
                borderColor: BORDER,
                alignItems: "center",
                justifyContent: "center",
                opacity: locked || winnerCount <= 1 ? 0.4 : 1,
              },
              cursorWeb,
            ]}
          >
            <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 14, color: W }}>−</Text>
          </Pressable>
          <Text style={{ width: 28, textAlign: "center", fontFamily: FONTS.displayMedium, fontSize: 20, color: AMBER }}>
            {winnerCount}
          </Text>
          <Pressable
            onPress={() => setCount(winnerCount + 1)}
            disabled={locked || winnerCount >= 10}
            style={[
              {
                width: 30,
                height: 30,
                borderRadius: 4,
                borderWidth: 1,
                borderColor: BORDER,
                alignItems: "center",
                justifyContent: "center",
                opacity: locked || winnerCount >= 10 ? 0.4 : 1,
              },
              cursorWeb,
            ]}
          >
            <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 14, color: W }}>+</Text>
          </Pressable>
        </View>
      </View>

      <View style={{ marginTop: 14, gap: 8 }}>
        {rows.map((r, i) => (
          <View
            key={i}
            style={{
              paddingHorizontal: 14,
              paddingVertical: 10,
              borderRadius: 4,
              borderWidth: 1,
              borderColor: BORDER,
              backgroundColor: BLACK_SURFACE,
              flexDirection: "row",
              alignItems: "center",
              gap: 12,
            }}
          >
            <View
              style={{
                width: 30,
                height: 30,
                borderRadius: 4,
                backgroundColor: "rgba(181,135,58,0.12)",
                borderWidth: 1,
                borderColor: "rgba(181,135,58,0.32)",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 14, color: AMBER }}>
                {r.place}
              </Text>
            </View>
            <TextInput
              value={r.label}
              onChangeText={(v) => setRow(i, { label: v })}
              editable={!locked}
              placeholder={defaultPlaceLabel(r.place)}
              placeholderTextColor={WL}
              style={{
                flex: 1,
                paddingHorizontal: 10,
                paddingVertical: 6,
                color: W,
                fontFamily: FONTS.body,
                fontSize: 12.5,
                backgroundColor: "rgba(0,0,0,0.3)",
                borderRadius: 3,
              }}
            />
            <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 13, color: WM }}>$</Text>
            <TextInput
              value={r.amountUsd}
              onChangeText={(v) => setRow(i, { amountUsd: v.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1") })}
              editable={!locked}
              keyboardType="decimal-pad"
              placeholder="0.00"
              placeholderTextColor={WL}
              style={{
                width: 110,
                textAlign: "right",
                paddingHorizontal: 10,
                paddingVertical: 6,
                color: W,
                fontFamily: FONTS.displayMedium,
                fontSize: 17,
                backgroundColor: "rgba(0,0,0,0.3)",
                borderRadius: 3,
              }}
            />
          </View>
        ))}
      </View>

      <Text style={{ marginTop: 8, fontFamily: FONTS.body, fontSize: 11, color: WL }}>
        Scholarship total: ${(totalCents / 100).toFixed(2)}
      </Text>

      {/* How the team's prize gets split. Coravu policy: always equal
          split among team members. Each member gets their own
          disbursement and their own 1099 (if their share exceeds $600),
          which is the cleanest tax-reporting path. Captain-takes-all
          mode was removed — it created tax/coordination headaches with
          no real client upside. The `team_split_policy` column still
          exists in the DB; legacy rows that were `captain_takes_all`
          aren't writable from the UI anymore but still display. */}
      <View style={{ marginTop: 14, padding: 12, borderRadius: 4, borderWidth: 1, borderColor: BORDER, backgroundColor: "rgba(255,255,255,0.02)" }}>
        <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 9.5, letterSpacing: 1.6, textTransform: "uppercase", color: WM }}>
          How the prize is split
        </Text>
        <Text style={{ marginTop: 6, fontFamily: FONTS.body, fontSize: 12.5, lineHeight: 18, color: WS }}>
          Each winning team's prize is split equally among its members. A 4-person team winning $5,000 gets four $1,250 disbursements — Coravu pays each member directly.
        </Text>
      </View>

      <View
        style={{ marginTop: 18, flexDirection: "row", alignItems: "center", gap: 12, flexWrap: "wrap" }}
      >
        <View style={{ flex: 1 }} />
        {statusMsg ? (
          <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11, color: SAGE }}>{statusMsg}</Text>
        ) : null}
        {errMsg ? (
          <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11, color: RED }}>{errMsg}</Text>
        ) : null}
        <Pressable
          onPress={() => void save()}
          disabled={saving || locked}
          style={[
            {
              paddingHorizontal: 18,
              paddingVertical: 10,
              borderRadius: 3,
              backgroundColor: AMBER,
              opacity: saving || locked ? 0.4 : 1,
            },
            cursorWeb,
          ]}
        >
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 11,
              letterSpacing: 1.6,
              textTransform: "uppercase",
              color: FOREST,
            }}
          >
            {saving ? "Saving…" : "Save winners"}
          </Text>
        </Pressable>
      </View>
    </View>
  );
}

function SplitOption({
  label,
  description,
  selected,
  disabled,
  onPress,
}: {
  label: string;
  description: string;
  selected: boolean;
  disabled: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      style={[
        {
          paddingHorizontal: 14,
          paddingVertical: 12,
          borderRadius: 4,
          borderWidth: 1.5,
          borderColor: selected ? AMBER : BORDER,
          backgroundColor: selected ? "rgba(181,135,58,0.08)" : "rgba(255,255,255,0.02)",
          opacity: disabled ? 0.5 : 1,
        },
        Platform.OS === "web" && !disabled ? ({ cursor: "pointer" } as const) : null,
      ]}
    >
      <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 13, color: selected ? AMBER : W }}>
        {selected ? "✓ " : ""}
        {label}
      </Text>
      <Text style={{ marginTop: 3, fontFamily: FONTS.body, fontSize: 11.5, color: WM, lineHeight: 17 }}>
        {description}
      </Text>
    </Pressable>
  );
}

function defaultPlaceLabel(place: number): string {
  const suffix = place === 1 ? "st" : place === 2 ? "nd" : place === 3 ? "rd" : "th";
  return place + suffix + " place";
}

// Read-only view rendered when a client opens an admin-managed RM case.
// Every field is display-only; no save/submit/delete affordances. The
// banner directs them to their Coravu account rep for change requests.
function AdminManagedRmReadonlyView({ row }: { row: ClientCompetitionListRow }) {
  // Local palette constants — the rest of the file uses module-scope
  // constants (BLACK, AMBER, etc.) that aren't in scope inside this
  // function. Inlining the few we need keeps this self-contained.
  const C_BLACK = THEME.pageBg;
  const C_FOREST_MID = COLORS.FOREST_MID.hex;
  const C_AMBER = THEME.accent;
  const C_LINEN = THEME.textOnSurface;
  const C_W = "rgba(255,255,255,1)";
  const C_WS = "rgba(255,255,255,0.78)";
  const C_BORDER = "rgba(255,255,255,0.07)";

  const tierLabel = row.tier ? (row.tier === "unlimited" ? "Unlimited students" : `${row.tier} students`) : "Not yet set";
  const prizeTotalCents = Array.isArray(row.prize_structure)
    ? row.prize_structure.reduce((s, p) => s + (typeof p?.amount_cents === "number" ? p.amount_cents : 0), 0)
    : 0;
  const scholarshipLabel =
    prizeTotalCents > 0
      ? `$${(prizeTotalCents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : "Not yet set";

  return (
    <ScrollView style={{ flex: 1, backgroundColor: C_BLACK }} contentContainerStyle={{ paddingBottom: 64 }}>
      <View style={{ padding: 24, maxWidth: 760 }}>
        {/* Banner */}
        <View
          style={{
            padding: 18,
            borderRadius: 6,
            borderWidth: 1,
            borderColor: "rgba(181,135,58,0.32)",
            backgroundColor: "rgba(181,135,58,0.06)",
          }}
        >
          <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 10, letterSpacing: 2.4, textTransform: "uppercase", color: C_AMBER }}>
            🏛️ Coravu is managing this case for you
          </Text>
          <Text style={{ marginTop: 8, fontFamily: FONTS.body, fontSize: 13, lineHeight: 19, color: C_WS }}>
            Coravu staff set this up and run it day-to-day. To request changes, contact{" "}
            <Text style={{ color: C_AMBER }}>admin@coravu.com</Text> or your Coravu account rep.
          </Text>
        </View>

        {/* Header */}
        <Text style={{ marginTop: 22, fontFamily: FONTS.bodyBold, fontSize: 10, letterSpacing: 3, textTransform: "uppercase", color: C_AMBER }}>
          Your Coravu-Managed case
        </Text>
        <Text style={{ marginTop: 6, fontFamily: FONTS.displayLight, fontSize: 28, lineHeight: 32, color: C_LINEN }}>
          {row.name}
        </Text>
        <View style={{ marginTop: 8, flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
          <RoChip>{`Status · ${row.status}`}</RoChip>
          <RoChip>{`Tier · ${tierLabel}`}</RoChip>
        </View>

        {/* Brief */}
        <RoSection label="Brief">
          <Text style={{ fontFamily: FONTS.body, fontSize: 13, lineHeight: 20, color: C_LINEN }}>
            {row.brief?.trim() || "Coravu will add this shortly."}
          </Text>
        </RoSection>

        <RoSection label="Deliverable description">
          <Text style={{ fontFamily: FONTS.body, fontSize: 13, lineHeight: 20, color: C_LINEN }}>
            {row.deliverable_description?.trim() || "Coravu will add this shortly."}
          </Text>
        </RoSection>

        <RoSection label="Scholarship pool">
          <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 22, color: C_W }}>{scholarshipLabel}</Text>
        </RoSection>

        <RoSection label="Schedule">
          <RoMeta k="Opens" v={row.opens_at ? new Date(row.opens_at).toLocaleString() : "Not yet set"} />
          <RoMeta k="Closes" v={row.closes_at ? new Date(row.closes_at).toLocaleString() : "Not yet set"} />
          <RoMeta k="Team size" v={`${row.team_size_min}–${row.max_team_size}`} />
        </RoSection>

        {/* Footer CTA */}
        <View
          style={{
            marginTop: 28,
            padding: 18,
            borderRadius: 6,
            borderWidth: 1,
            borderColor: C_BORDER,
            backgroundColor: C_FOREST_MID,
          }}
        >
          <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12.5, lineHeight: 19, color: C_WS }}>
            Need a change? Reply to your last email from Coravu, or write to{" "}
            <Text style={{ color: C_AMBER }}>admin@coravu.com</Text> — your account rep will get back to you.
          </Text>
        </View>
      </View>
    </ScrollView>
  );
}

function RoSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <View style={{ marginTop: 22 }}>
      <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 9.5, letterSpacing: 1.6, textTransform: "uppercase", color: "rgba(255,255,255,0.55)", marginBottom: 8 }}>
        {label}
      </Text>
      <View
        style={{
          padding: 14,
          borderRadius: 4,
          borderWidth: 1,
          borderColor: "rgba(255,255,255,0.07)",
          backgroundColor: COLORS.FOREST_MID.hex,
        }}
      >
        {children}
      </View>
    </View>
  );
}

function RoMeta({ k, v }: { k: string; v: string }) {
  return (
    <View style={{ flexDirection: "row", marginTop: 4, gap: 8 }}>
      <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11, color: "rgba(255,255,255,0.55)", minWidth: 110 }}>{k}</Text>
      <Text style={{ flex: 1, fontFamily: FONTS.body, fontSize: 12, color: "rgba(255,255,255,0.85)" }}>{v}</Text>
    </View>
  );
}

function RoChip({ children }: { children: string }) {
  return (
    <View style={{ paddingHorizontal: 9, paddingVertical: 3, borderRadius: 999, borderWidth: 1, borderColor: "rgba(255,255,255,0.18)" }}>
      <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 10.5, color: "rgba(255,255,255,0.78)" }}>{children}</Text>
    </View>
  );
}

// Promo code input — lives inline in the editor body. Loads any
// previously-saved pending_promo_code on mount, validates it server-side,
// and shows the discount label. Apply persists the code via setPendingPromoCode
// so it survives a page reload and so the admin's Quote Builder can pre-fill
// from it. Remove (x) clears both the local state and the DB column.
function ClientPromoCodeInput({
  competitionId,
  editable,
}: {
  competitionId: string;
  editable: boolean;
}) {
  const C_AMBER = THEME.accent;
  const C_SAGE = COLORS.SAGE.hex;
  const C_WL = "rgba(255,255,255,0.28)";
  const C_W = "rgba(255,255,255,1)";
  const C_WS = "rgba(255,255,255,0.78)";
  const C_WM = "rgba(255,255,255,0.55)";
  const C_RED = THEME.destructive;
  const C_BORDER = "rgba(255,255,255,0.07)";
  const C_BLACK_BG = "rgba(0,0,0,0.4)";

  const [input, setInput] = useState("");
  const [applied, setApplied] = useState<{ code: string; result: PromoValidation } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [warn, setWarn] = useState<string | null>(null);

  // Hydrate any previously-applied pending code on mount + revalidate so we
  // catch codes that have expired between the client typing them and the
  // admin reviewing the case.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const stored = await getPendingPromoCode(competitionId);
      if (cancelled || !stored) return;
      const result = await validatePromoCode(stored, competitionId);
      if (cancelled) return;
      if (result.valid) {
        setApplied({ code: stored, result });
        setWarn(null);
      } else {
        setApplied({ code: stored, result });
        setWarn(
          "This code is no longer valid — Coravu will contact you about the discount if it still applies.",
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [competitionId]);

  async function onApply() {
    const code = input.trim().toUpperCase();
    if (!code) return;
    setBusy(true);
    setErr(null);
    setWarn(null);
    try {
      const result = await validatePromoCode(code, competitionId);
      if (!result.valid) {
        setErr(result.reason || "Code is not valid");
        return;
      }
      await setPendingPromoCode(competitionId, code);
      setApplied({ code, result });
      setInput("");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  async function onRemove() {
    setBusy(true);
    setErr(null);
    setWarn(null);
    try {
      await setPendingPromoCode(competitionId, null);
      setApplied(null);
      setInput("");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <View>
      <Text style={{ fontFamily: FONTS.body, fontSize: 12.5, lineHeight: 19, color: C_WS }}>
        Have a Coravu promo code? Enter it here and we&apos;ll apply the discount at approval time.
      </Text>
      <View style={{ marginTop: 12, flexDirection: "row", gap: 8, flexWrap: "wrap" }}>
        <TextInput
          value={input}
          onChangeText={(v) => setInput(v.toUpperCase())}
          placeholder="PROMO CODE"
          placeholderTextColor={C_WL}
          editable={editable && !applied}
          autoCapitalize="characters"
          style={
            {
              flex: 1,
              minWidth: 200,
              backgroundColor: C_BLACK_BG,
              borderWidth: 1,
              borderColor: C_BORDER,
              borderRadius: 4,
              paddingHorizontal: 14,
              paddingVertical: 11,
              color: C_W,
              fontFamily: "SpaceMono",
              fontSize: 14,
              letterSpacing: 1.6,
              opacity: applied ? 0.5 : 1,
              ...(Platform.OS === "web" ? ({ outlineStyle: "none" } as object) : {}),
            } as unknown as TextStyle
          }
        />
        {!applied ? (
          <Pressable
            onPress={() => void onApply()}
            disabled={busy || !editable || !input.trim()}
            style={[
              {
                paddingHorizontal: 16,
                paddingVertical: 11,
                borderRadius: 4,
                backgroundColor: C_AMBER,
                opacity: busy || !editable || !input.trim() ? 0.4 : 1,
              } as ViewStyle,
              Platform.OS === "web" && !busy && editable && !!input.trim() ? ({ cursor: "pointer" } as const) : null,
            ]}
          >
            <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 11, letterSpacing: 1.4, textTransform: "uppercase", color: THEME.sidebarBg }}>
              {busy ? "Checking…" : "Apply"}
            </Text>
          </Pressable>
        ) : null}
      </View>

      {applied?.result.valid ? (
        <View
          style={{
            marginTop: 12,
            padding: 12,
            borderRadius: 4,
            backgroundColor: "rgba(143,187,138,0.08)",
            borderWidth: 1,
            borderColor: "rgba(143,187,138,0.40)",
            flexDirection: "row",
            alignItems: "center",
            gap: 10,
          }}
        >
          <View style={{ flex: 1 }}>
            <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 11.5, color: C_SAGE, letterSpacing: 0.4 }}>
              ✓ {applied.code} applied
            </Text>
            <Text style={{ marginTop: 2, fontFamily: FONTS.body, fontSize: 12, color: C_WS }}>
              {applied.result.discount_label}
              {applied.result.discount_cents > 0
                ? ` — ${formatCents(applied.result.discount_cents)} discount`
                : " — discount calculated when Coravu generates your quote"}
            </Text>
          </View>
          {editable ? (
            <Pressable
              onPress={() => void onRemove()}
              disabled={busy}
              style={[
                {
                  paddingHorizontal: 10,
                  paddingVertical: 6,
                  borderRadius: 3,
                  borderWidth: 1,
                  borderColor: C_BORDER,
                  backgroundColor: "rgba(255,255,255,0.02)",
                } as ViewStyle,
                Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
              ]}
            >
              <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11, color: C_WS }}>Remove</Text>
            </Pressable>
          ) : null}
        </View>
      ) : null}

      {warn ? (
        <View
          style={{
            marginTop: 12,
            padding: 12,
            borderRadius: 4,
            backgroundColor: "rgba(181,135,58,0.08)",
            borderWidth: 1,
            borderColor: "rgba(181,135,58,0.40)",
          }}
        >
          <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12, color: C_AMBER }}>{warn}</Text>
        </View>
      ) : null}

      {err ? (
        <Text style={{ marginTop: 8, fontFamily: FONTS.bodyMedium, fontSize: 12, color: C_RED }}>{err}</Text>
      ) : null}

      {!editable && !applied ? (
        <Text style={{ marginTop: 8, fontFamily: FONTS.body, fontSize: 11.5, color: C_WM }}>
          Promo codes can only be applied while a case is in draft.
        </Text>
      ) : null}
    </View>
  );
}
